// API client for the teacher app. Same pattern as the student app's client —
// token in localStorage, Bearer header on every call. The backend accepts our
// Bearer token on every existing teacher route (see backend
// services/auth/access_control.ts), so we can reuse /tests, /online-tests/*,
// /student/* (for submissions data) without parallel endpoints.

import { downscaleImage, downscaleImages } from "./imageDownscale";
import { apiUrl } from "../src/brand";

const TOKEN_KEY = "app_teacher_token";
const TEACHER_KEY = "app_teacher_meta";

export interface TeacherMeta {
  id: string;
  name: string;
  email: string | null;
  role: string;
  school_id: string;
  school_name: string;
}

// The public doors — every endpoint that is called with deliberately no session
// yet. A 401 from one of these means "that credential is wrong" (a bad password,
// a mistyped OTP), NOT "your session ended", and must never sign anybody out.
//
const PUBLIC_AUTH_PATHS = [
  "/teacher/login",
  "/teacher/magic-link/consume",
  "/teacher/otp/",
  "/teacher/register",
  "/teacher/forgot-password",
  "/teacher/status",
];

let sessionEndHandled = false;

// A 401 on any authenticated call means the session is gone: expired, revoked
// by an admin resetting the password, or wiped with the browser storage.
//
// Nothing used to act on that. isAuthed() only checks that a *string* is
// present in localStorage, and an expired token is still a string — so the
// router let the page mount, every request 401'd, and the screen filled up with
// the server's raw word "unauthenticated", which reads as the app being broken.
//
// A hard navigation rather than a react-router push, deliberately: the tree is
// mid-render against a dead session and nothing on it is worth carrying into a
// sign-in form.
function onSessionEnded(): void {
  if (sessionEndHandled) return;   // several requests in flight → one redirect
  sessionEndHandled = true;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TEACHER_KEY);
  if (location.pathname.startsWith("/login")) return;
  const next = encodeURIComponent(location.pathname + location.search);
  location.replace(`/login?expired=1&next=${next}`);
}

// startsWith, not ===, so the OTP family and any query string these grow later
// stay covered without another edit here.
const isPublicAuthPath = (path: string) =>
  PUBLIC_AUTH_PATHS.some((p) => path.startsWith(p));

function authHeaders(): HeadersInit {
  const t = localStorage.getItem(TOKEN_KEY);
  const h: Record<string, string> = t ? { Authorization: `Bearer ${t}` } : {};
  return h;
}

export async function http<T>(method: string, path: string, body?: any): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: { "content-type": "application/json", ...authHeaders() },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401 && !isPublicAuthPath(path)) onSessionEnded();
    let msg = `${method} ${path} → ${res.status}`;
    let body: any = null;
    try { body = await res.json(); if (body?.error) msg = body.error; } catch {}
    // Attach the parsed error body so callers can read structured fields
    // (e.g. the check-out rejection carries { distance, radius_m, can_override }).
    throw Object.assign(new Error(msg), { status: res.status, body });
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// Multipart variant — used by image OCR + per-question image upload.
async function httpMultipart<T>(path: string, form: FormData, method = "POST"): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: authHeaders(), // do NOT set content-type; browser fills it
    body: form,
  });
  if (!res.ok) {
    if (res.status === 401) onSessionEnded();
    let msg = `${method} ${path} → ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
    throw Object.assign(new Error(msg), { status: res.status });
  }
  return res.json();
}

// ── Auth ────────────────────────────────────────────────────────────────────
export async function login(username: string, password: string): Promise<TeacherMeta> {
  const r = await http<{ token: string; teacher: TeacherMeta }>("POST", "/teacher/login", { username, password });
  localStorage.setItem(TOKEN_KEY, r.token);
  localStorage.setItem(TEACHER_KEY, JSON.stringify(r.teacher));
  sessionEndHandled = false;   // re-arm the expiry redirect
  return r.teacher;
}

// Redeem a one-tap sign-in link issued by an admin. The response shape is
// identical to login(), so the session is stored through the same two keys and
// nothing downstream can tell the difference.
export async function signInWithMagicLink(token: string): Promise<TeacherMeta> {
  const r = await http<{ token: string; teacher: TeacherMeta }>(
    "POST", "/teacher/magic-link/consume", { token },
  );
  localStorage.setItem(TOKEN_KEY, r.token);
  localStorage.setItem(TEACHER_KEY, JSON.stringify(r.teacher));
  sessionEndHandled = false;   // re-arm the expiry redirect
  return r.teacher;
}

export async function logout(): Promise<void> {
  try { await http<void>("POST", "/teacher/logout"); } catch {}
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TEACHER_KEY);
}

export function currentTeacher(): TeacherMeta | null {
  const raw = localStorage.getItem(TEACHER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export const isAuthed = () => !!localStorage.getItem(TOKEN_KEY);

// ── WhatsApp OTP login ──────────────────────────────────────────────────────
// Two steps. The teacher picks their name from the directory, then confirms
// with the last 4 digits of their own phone before any message is sent — the
// directory is public, so without that check anyone could fire codes at any
// teacher and burn their daily cap. The last 4 digits are never returned by
// the API; the teacher supplies them from memory.

export interface DirectoryEntry { id: string; name: string; role: string }

export interface OtpSent {
  status: "sent";
  phone_mask: string;              // "+00 ****0000" — safe to display
  channel: "sms" | "whatsapp";     // server-controlled; UI names it, doesn't pick it
  expires_in_s: number;
  max_attempts: number;
}

export const channelLabel = (c?: "sms" | "whatsapp") =>
  c === "whatsapp" ? "WhatsApp" : "SMS";

export function otpDirectory(school_id: string): Promise<DirectoryEntry[]> {
  return http<DirectoryEntry[]>("GET", `/teacher/otp/directory?school_id=${encodeURIComponent(school_id)}`);
}

export function otpStart(user_id: string, last4: string): Promise<OtpSent> {
  return http<OtpSent>("POST", "/teacher/otp/start", { user_id, last4 });
}

export async function otpVerify(user_id: string, code: string): Promise<TeacherMeta> {
  const r = await http<{ token: string; teacher: TeacherMeta }>(
    "POST", "/teacher/otp/verify", { user_id, code },
  );
  localStorage.setItem(TOKEN_KEY, r.token);
  localStorage.setItem(TEACHER_KEY, JSON.stringify(r.teacher));
  sessionEndHandled = false;   // re-arm the expiry redirect
  return r.teacher;
}

// ── Self-registration ────────────────────────────────────────────────────────
export interface SchoolOption { id: string; name: string }
export interface RegisterCatalog {
  class_sections: { id: string; class_level: string; section: string | null; academic_year: string; label: string }[];
  subjects: { id: string; name: string; short_code: string | null }[];
}
export type SelfRegRole = "teacher" | "hod" | "coordinator" | "support_staff" | "admin";
export interface RegisterInput {
  school_id: string;
  name: string;
  phone: string;
  email: string;
  password: string;
  role: SelfRegRole;
  boarding: boolean;
  assignments: { class_section_id: string; subject_id: string }[];
}
export type RegistrationStatus = "pending" | "active" | "suspended" | "unknown";

export function listSchools(): Promise<SchoolOption[]> {
  return http<SchoolOption[]>("GET", "/teacher/schools");
}
export function getRegisterCatalog(schoolId: string): Promise<RegisterCatalog> {
  return http<RegisterCatalog>("GET", `/teacher/catalog?school_id=${encodeURIComponent(schoolId)}`);
}
export function registerTeacher(body: RegisterInput): Promise<{ status: "pending" }> {
  return http<{ status: "pending" }>("POST", "/teacher/register", body);
}
export function checkRegistrationStatus(phone: string): Promise<{ status: RegistrationStatus }> {
  return http<{ status: RegistrationStatus }>("GET", `/teacher/status?phone=${encodeURIComponent(phone)}`);
}

// ── Forgot password ──────────────────────────────────────────────────────────
export function forgotPassword(body: {
  phone: string; new_password: string; new_password_confirm: string;
}): Promise<{ status: "pending" }> {
  return http<{ status: "pending" }>("POST", "/teacher/forgot-password", body);
}
export function forgotPasswordStatus(phone: string): Promise<{ status: "pending" | "approved" | "rejected" | "none" }> {
  return http("GET", `/teacher/forgot-password/status?phone=${encodeURIComponent(phone)}`);
}

// ── Password-reset approvals (admin / HoD) ───────────────────────────────────
export interface PasswordResetRow {
  id: string;
  status: "pending" | "approved" | "rejected";
  requested_at: string;
  decided_at: string | null;
  teacher: { id: string; name: string; phone: string | null; email: string | null; role: string };
}
export function listPasswordResets(status: "pending" | "approved" | "rejected" = "pending"): Promise<PasswordResetRow[]> {
  return http<PasswordResetRow[]>("GET", `/password-resets?status=${status}`);
}
export function approvePasswordReset(id: string): Promise<{ ok: true; status: "approved" }> {
  return http("POST", `/password-resets/${encodeURIComponent(id)}/approve`, {});
}
export function rejectPasswordReset(id: string): Promise<{ ok: true; status: "rejected" }> {
  return http("POST", `/password-resets/${encodeURIComponent(id)}/reject`, {});
}

// ── Parent portal approvals (admin + HoD) ───────────────────────────────────
// Parents self-register in the Parent app with status='pending'. Approving
// verifies their claimed children and opens the account. These hit the same
// /admin/parents endpoints the admin panel uses; the backend gate is admin|hod
// (see backend/src/routes/admin_parents.ts), so an ordinary teacher calling
// these gets a 403.

export type ParentAccountStatus = "pending" | "active" | "suspended";

export interface ParentAccountRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  status: ParentAccountStatus;
  requested_at: string;
  approved_at: string | null;
  last_login_at: string | null;
  /** How many children this parent claimed, and how many are verified. */
  link_count: number;
  verified_count: number;
}

export interface ParentClaimLink {
  id: string;
  parent_id: string;
  student_id: string;
  student_name: string;
  roll_no: string;
  class_label: string;
  relation: "father" | "mother" | "guardian";
  self_claimed: boolean;
  verified_at: string | null;
}

export type ParentAccountDetail = ParentAccountRow & { links: ParentClaimLink[] };

export function listParentAccounts(
  status: ParentAccountStatus = "pending",
): Promise<ParentAccountRow[]> {
  return http<ParentAccountRow[]>("GET", `/admin/parents?status=${status}`);
}

export function getParentAccount(id: string): Promise<ParentAccountDetail> {
  return http<ParentAccountDetail>("GET", `/admin/parents/${encodeURIComponent(id)}`);
}

export function actOnParentAccount(
  id: string,
  action: "approve" | "reject" | "suspend" | "unsuspend",
): Promise<{ ok: true }> {
  return http("POST", `/admin/parents/${encodeURIComponent(id)}/${action}`, {});
}

/** Drop one wrongly-claimed child without rejecting the whole account. */
export function removeParentClaim(parentId: string, linkId: string): Promise<{ ok: true }> {
  return http(
    "DELETE",
    `/admin/parents/${encodeURIComponent(parentId)}/links/${encodeURIComponent(linkId)}`,
  );
}

// ── Profile (/me) ───────────────────────────────────────────────────────────
// Returned by the backend's GET /api/me with the teacher's authenticated
// session attached. Includes the scope grid the admin set up.
export interface ScopeRow {
  class_section_id: string;
  subject_id: string;
  class_label: string;        // "X-A"
  subject_name: string;
  academic_year: string;
}
export interface ProfileResponse {
  user: { id: string; name: string; email: string | null; phone: string | null;
          role: "teacher" | "hod" | "admin" | "coordinator" | "boarder" | "support_staff"; initials: string; avatar_url: string | null };
  school: { id: string; name: string; academic_year: string } | null;
  scopes: ScopeRow[];
  unlimited: boolean;
}
export function fetchProfile(): Promise<ProfileResponse> {
  return http<ProfileResponse>("GET", "/me");
}

// Upload the caller's profile photo. Returns the new avatar URL.
export function uploadAvatar(file: Blob): Promise<{ avatar_url: string }> {
  const form = new FormData();
  form.append("photo", file, "avatar.jpg");
  return httpMultipart<{ avatar_url: string }>("/me/avatar", form);
}

export function removeAvatar(): Promise<void> {
  return http<void>("DELETE", "/me/avatar");
}

// ── Test library ────────────────────────────────────────────────────────────
export interface TestRow {
  id: string;          // display_id, e.g. T-2407
  _uuid: string;
  title: string;
  className: string;
  subject: string;
  // Online-test format. Null for legacy paper-eval tests that pre-date the
  // online flow. Used to render a format pill next to the test id.
  type?: "blitz" | "classical" | "descriptive" | null;
  classLabel?: string;       // rolled-up "X-A, X-B +1" for multi-section
  // Section set this test targets — populated for every row by the backend
  // multi-section refactor. Used by the edit sheet to pre-select chips.
  class_section_ids?: string[];
  topic?: string;
  chapter?: string;
  date: string | null;
  status: string;
  language: string;
  questions: number;
  students: number;
  reviewed: number;
  // Backend now stamps these on every list row so the mobile UI can flag
  // "your test" + reflect that submissions are author-only.
  created_by?: string | null;
  created_by_name?: string | null;
  mine?: boolean;
  // Auto-expiry: effective expires_at (server-derived from scheduled_date or
  // overridden by revive) and a boolean is_expired flag for convenience. The
  // mobile library shows expired tests under "Past" with a badge + Revive
  // action; the student portal blocks new attempts entirely.
  expires_at?: string | null;
  is_expired?: boolean;
}

// `/tests` returns the full library — we filter to online tests client-side
// (status published / live / closed and type set). The backend doesn't expose
// `type` on the list endpoint today, so we just hide drafts that have no Qs.
export async function listTests(): Promise<TestRow[]> {
  return http<TestRow[]>("GET", "/tests");
}

// PUT /tests/:displayId — author-gated on the server, so passing this from a
// non-owner returns 403 and the caller surfaces the error.
export interface TestMetaUpdate {
  title?: string;
  class_section_ids?: string[];
  scheduled_date?: string | null;
  topic?: string | null;
  chapter?: string | null;
  language?: string;
}
export async function updateTestMeta(displayId: string, body: TestMetaUpdate): Promise<void> {
  await http<{ ok: true }>("PUT", `/tests/${encodeURIComponent(displayId)}`, body);
}

// DELETE /tests/:displayId — cascades to questions, attempts, evaluations.
// Backend gates by tests.created_by, so only the author or HoD/admin succeeds.
export async function deleteTest(displayId: string): Promise<void> {
  await http<{ ok: true }>("DELETE", `/tests/${encodeURIComponent(displayId)}`);
}

// POST /tests/:displayId/revive — extends expires_at by 2 days (anchored on
// now() when the test had already lapsed). Author-only.
export async function reviveTest(displayId: string): Promise<{ expires_at: string | null }> {
  return http<{ expires_at: string | null }>("POST", `/tests/${encodeURIComponent(displayId)}/revive`, {});
}

// ── Online-test creation ────────────────────────────────────────────────────
//   classical   — global timer, MCQ
//   blitz       — per-question timer, MCQ
//   descriptive — global timer, no MCQ; student uploads photo(s) of their
//                 handwritten answer per question. Teacher grades manually.
export type TestType = "blitz" | "classical" | "descriptive";

// Per-section question type. Teacher picks one of these when building each
// section in the create wizard. Maps directly to questions.type in the DB.
//
// "passage" is a UI-only convenience type: the teacher pastes a passage in
// one box and the questions in another, and the wizard auto-creates a single
// passage block linked to every question in the section. Before the payload
// hits the backend it's downgraded to "mcq" + a populated passages[] (the
// backend has no concept of a "passage section type"; it just sees MCQ +
// passages).
export type SectionQuestionType = "mcq" | "short" | "long" | "passage";

export interface ParsedOption  { letter: string; text: string; is_correct: boolean; image?: string | null }
export interface ParsedQuestion {
  q_no: number; text: string; marks: number;
  // For descriptive tests the array is empty (no MCQ choices).
  options: ParsedOption[];
  // Detailed solution shown on the student's report after submission.
  // Either rich text (Markdown + LaTeX, same renderer as the question) or
  // one+ image URLs, or both.
  expected_answer?: string | null;
  images?: { question?: string[]; solution?: string[] } | null;
  // Tags this question as belonging to a passage block. References
  // ParsedPassage.id in the same section. Null/omitted = standalone.
  passage_id?: string | null;
}
export interface ParsedPassage {
  // Client-side tag (any unique string). The backend assigns the real UUID
  // on persist; the tag is only used to link questions to passages in-payload.
  id: string;
  text: string;
  // Optional images uploaded via uploadQuestionImage() — same bucket as
  // question images. Order matters; the renderer shows them in sequence.
  images?: string[];
}
export interface ParsedSection  {
  label: string;
  title?: string | null;
  // Teacher's declared question type for this section. Drives validation
  // (MCQ needs options, descriptive doesn't) and ends up on questions.type
  // in the DB. Optional only for back-compat with legacy single-type parses.
  type?: SectionQuestionType;
  // Default marks/question this section was authored with. Persisted to
  // test_sections.marks_per_question; the per-question `marks` cell may still
  // override on a question-by-question basis.
  marks_per_question?: number | null;
  // Reading-comp passages owned by this section. Only valid for MCQ sections;
  // children share the passage via `passage_id`. Empty/undefined when none.
  passages?: ParsedPassage[];
  questions: ParsedQuestion[];
}
export interface ParsedTest {
  title: string;
  className?: string | null;
  subject?:   string | null;
  // Multi-section: the picker stores selected section uuids here. The backend
  // joins these into test_class_sections and UNIONs enrollment across them.
  class_section_ids?: string[];
  subject_id?: string | null;
  topic?:     string | null;
  chapter?:   string | null;
  scheduled_date?: string | null;
  sections: ParsedSection[];
  warnings?: string[];
}

// ── Catalog ─────────────────────────────────────────────────────────────────
// Live source of truth for class sections + subjects in this school. Replaces
// the old hardcoded CLASS_OPTIONS / SUBJECT_OPTIONS arrays.
export interface ClassSection {
  id: string;
  class_level: string;
  section: string | null;
  academic_year: string;
  label: string;
}
export interface Subject { id: string; name: string; short_code: string | null }

export async function listClassSections(): Promise<ClassSection[]> {
  return http<ClassSection[]>("GET", "/catalog/class-sections");
}
export async function listSubjects(): Promise<Subject[]> {
  return http<Subject[]>("GET", "/catalog/subjects");
}

// (class_section_id × subject_id) pairs the caller is allowed to author tests
// for. Used by the create wizard to disable section chips that don't pair with
// the chosen subject.
export interface ScopePair { class_section_id: string; subject_id: string }
export async function listMyScope(): Promise<{ unlimited: boolean; pairs: ScopePair[] }> {
  return http<{ unlimited: boolean; pairs: ScopePair[] }>("GET", "/catalog/my-scope");
}

// Chapter + topic catalogs. Admin curates these in the admin panel; both
// dropdowns in test create/edit fall back to "(none)" when the catalog is
// empty for the chosen (class, subject) / chapter.
export interface ChapterRow {
  id: string;
  chapter_number: number;
  chapter_name: string;
  class_level: string | null;
}
export interface TopicRow {
  id: string;
  topic_number: number;
  topic_name: string;
}

export async function listChapters(args: { subject_id: string; class_level?: string }): Promise<ChapterRow[]> {
  const q = new URLSearchParams({ subject_id: args.subject_id });
  if (args.class_level) q.set("class_level", args.class_level);
  return http<ChapterRow[]>("GET", `/catalog/chapters?${q.toString()}`);
}

export async function listTopics(chapter_id: string): Promise<TopicRow[]> {
  return http<TopicRow[]>("GET", `/catalog/topics?chapter_id=${encodeURIComponent(chapter_id)}`);
}

// Multi-select helpers. The DB columns `tests.chapter` / `tests.topic` stay
// TEXT (no schema migration); we encode multiple selections as a
// comma-separated list. Legacy single-name rows ("Hydrocarbons") parse as a
// one-element array and re-serialise identically, so a teacher who never
// adds a second chapter never sees a difference. Commas inside chapter
// names are not supported — chapter names rarely contain them, but admins
// should avoid them.
export function parseChapterTopicList(raw: string | null | undefined): string[] {
  if (raw == null) return [];
  const s = String(raw).trim();
  if (!s) return [];
  return s.split(/\s*,\s*/).map((x) => x.trim()).filter(Boolean);
}
export function serializeChapterTopicList(items: string[] | null | undefined): string | null {
  const xs = (items || []).map((s) => String(s).trim()).filter(Boolean);
  if (xs.length === 0) return null;
  // Dedupe while preserving the picker's selection order — useful when
  // the chapter list spans multiple chapters and we don't want repeats.
  const seen = new Set<string>();
  const uniq: string[] = [];
  for (const x of xs) { if (!seen.has(x)) { seen.add(x); uniq.push(x); } }
  return uniq.join(", ");
}

export async function formatFromText(args: { raw_text: string; type: TestType; language?: string }): Promise<ParsedTest> {
  const r = await http<{ parsed: ParsedTest }>("POST", "/online-tests/format", args);
  return r.parsed;
}

// Format one or more photos in a single Gemini Vision call. The backend
// reads them in the order they're appended — so the order here matches the
// order the teacher arranged them in the UI.
//
// Photos are downscaled in-browser before upload (long edge → 2000 px, JPEG
// q=0.85) so a stack of phone shots stays well under Gemini's inline-image
// budget. The transform is a no-op for already-small images; HEIC and other
// undecodable formats pass through untouched.
export async function formatFromImage(args: { files: File[]; type: TestType; language?: string }): Promise<ParsedTest> {
  if (args.files.length === 0) throw new Error("at least one photo is required");
  const optimized = await downscaleImages(args.files);
  const form = new FormData();
  for (const f of optimized) form.append("files", f);
  form.append("type", args.type);
  if (args.language) form.append("language", args.language);
  const r = await httpMultipart<{ parsed: ParsedTest }>("/online-tests/parse-image", form);
  return r.parsed;
}

export async function uploadQuestionImage(file: File): Promise<{ url: string }> {
  // Downscale before upload so a teacher's phone shot doesn't bloat the
  // /question-images bucket. Same 500-1000 KB target the descriptive answer
  // path uses — keeps figures legible without storing 5 MB JPEGs.
  const shrunk = await downscaleImage(file);
  const form = new FormData();
  form.append("file", shrunk);
  return httpMultipart<{ url: string }>("/online-tests/upload-image", form);
}

// ── Bulk answer-sheet upload ON BEHALF of a student (physical hand-in) ───────
// A student with no phone/scanner hands paper to the teacher, who scans
// + uploads it here into the attempt's bulk pile.
export async function uploadAttemptBulkImage(attemptId: string, file: File): Promise<{ url: string }> {
  const shrunk = await downscaleImage(file);
  const form = new FormData();
  form.append("file", shrunk);
  return httpMultipart<{ url: string }>(`/online-tests/attempts/${encodeURIComponent(attemptId)}/bulk-upload-image`, form);
}

export async function uploadAttemptBulkPdf(attemptId: string, file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  return httpMultipart<{ url: string }>(`/online-tests/attempts/${encodeURIComponent(attemptId)}/bulk-upload-pdf`, form);
}

export async function saveAttemptBulkUploads(
  attemptId: string, images: string[], pdf: string | null,
): Promise<{ ok: true; images: string[]; pdf: string | null }> {
  return http("PUT", `/online-tests/attempts/${encodeURIComponent(attemptId)}/bulk-uploads`, { images, pdf });
}

// Mirrors backend/src/services/engine_config.ts EditableEngineConfig. Kept
// inline here so the create wizard can build a Partial<...> without an HTTP
// round-trip; the backend re-merges with its own defaults via withDefaults().
export interface EditableEngineConfig {
  timing: {
    type: "global" | "sectional" | "per_question" | "none";
    duration_seconds: number;
    per_question_duration?: number;
    auto_submit_on_expiry: boolean;
    show_timer: boolean;
    hard_lock_on_timeout?: boolean;
  };
  navigation: {
    allow_back_tracking: boolean;
    allow_jumping: boolean;
    show_question_palette: boolean;
  };
  scoring: {
    correct_marks: number;
    wrong_marks: number;        // negative-marking value (positive number; "-1" → 1)
    unattempted_marks: number;
  };
  logic: {
    atomic_block_shuffle: boolean;
  };
}

export async function publishOnlineTest(args: {
  parsed: ParsedTest;
  publish?: boolean;
  // Teacher's overrides from the engine-config step. Merged with the server
  // default preset (see presetEngineConfig + sanitizeEditableConfig).
  engine_overrides?: Partial<EditableEngineConfig>;
  // How students hand in descriptive answers: 'per_question' (upload during the
  // exam) | 'bulk' (upload all photos + an optional PDF after submit). The
  // backend coerces it back to per_question for all-objective tests.
  submission_mode?: "per_question" | "bulk";
}): Promise<{ id: string; _uuid: string; published: boolean; enrolled: number }> {
  // Hoist class_section_ids / subject_id to top-level so the backend uses the
  // explicit ids instead of name-resolution from the parsed object.
  //
  // Derive a single test-level `type` from the section mix so the current
  // backend (which still expects one type per test) accepts the payload:
  //   all MCQ                  → "classical"
  //   any descriptive section  → "descriptive"
  // The per-section `type` on parsed.sections is the source of truth once the
  // backend is taught to read it.
  const anyDescriptive = args.parsed.sections.some(s => s.type === "short" || s.type === "long");
  const derivedType: TestType = anyDescriptive ? "descriptive" : "classical";
  const body: Record<string, unknown> = {
    parsed: args.parsed, type: derivedType, publish: !!args.publish,
  };
  if (args.parsed.class_section_ids?.length) body.class_section_ids = args.parsed.class_section_ids;
  if (args.parsed.subject_id) body.subject_id = args.parsed.subject_id;
  if (args.engine_overrides) body.engine_overrides = args.engine_overrides;
  if (args.submission_mode) body.submission_mode = args.submission_mode;
  return http("POST", "/online-tests", body);
}

// ── Submissions ─────────────────────────────────────────────────────────────
export interface StudentRow {
  student_id: string;
  name: string;
  roll_no: string;
  attempt_id: string | null;
  attempt_status: string | null;        // 'in_progress' | 'submitted' | 'reviewed'
  auto_score: number | null;
  teacher_score: number | null;
  max_marks: number | null;
  submitted_at: string | null;
}

// The backend's /tests/:displayId/students endpoint is paper-eval shaped; for
// online tests we'd rather query the student_attempts directly. The submissions
// route returns the same join the dashboard uses internally.
export async function listSubmissions(displayId: string): Promise<{ test: any; students: StudentRow[] }> {
  return http("GET", `/online-tests/${encodeURIComponent(displayId)}/submissions`);
}

// Latest retest request for this attempt + actionability flags. Surfaced on
// the teacher's attempt detail so AttemptPage can render the Approve / Deny
// banner without a second fetch. Status "none" means the student hasn't
// asked yet (banner stays hidden).
export interface AttemptRetestState {
  status: "none" | "pending" | "approved" | "denied";
  request_id: string | null;
  reason: string | null;
  created_at: string | null;
  decided_at: string | null;
  decision_note: string | null;
  consumed: boolean;
}

export interface AttemptDetail {
  attempt: {
    id: string; status: string;
    auto_score: number | null;
    teacher_score: number | null;
    max_marks: number | null;
    // Timestamps + the test's allotted-time budget so the report can render
    // both per-question dwell times (response.time_spent_ms) and the
    // attempt-level "12m 30s used / 30m allotted" line. allotted_seconds is
    // null when the test had no timer configured — the UI then hides the
    // "/ allotted" suffix.
    started_at: string | null;
    submitted_at: string | null;
    allotted_seconds: number | null;
    // Bulk submission: the attempt-level answer-sheet pile (photos + an optional
    // PDF) a student uploaded after a bulk-mode submit. Empty / null on
    // per_question tests. finalized_at null → the student may still be adding.
    submission_mode?: "per_question" | "bulk";
    bulk_response_images?: string[];
    bulk_response_pdf?: string | null;
    bulk_uploads_finalized_at?: string | null;
    // 'physical' → student handed paper in; the teacher uploads on their behalf.
    bulk_submission_method?: "self" | "physical" | null;
  };
  test: { id: string; title: string };
  student: { id: string; name: string; roll_no: string };
  sections: Array<{
    id: string; label: string; title: string | null;
    questions: Array<{
      id: string; q_no: string; text: string;
      type: string; marks: number;
      images?: { question?: string[]; solution?: string[] } | null;
      // Passage-grouped MCQs: passage_id is the FK; passage carries the text
      // + optional images inlined so the report can render the passage card
      // without a join. Both null on standalone questions.
      passage_id?: string | null;
      passage?: { id: string; text: string; images?: string[] } | null;
      options: Array<{ id: string; letter: string; text: string; is_correct: boolean }>;
      response: {
        selected_option_id: string | null;
        text_answer: string | null;
        // Descriptive submissions: URLs of the student's uploaded photo(s).
        // Empty array when the student didn't upload anything for this Q.
        response_images: string[];
        // Parallel array — same length as response_images once any annotation
        // has been saved. Index i is null (un-annotated) or a URL pointing at
        // the flattened render produced by AnnotateModal. Empty array means
        // no annotations on this submission.
        annotated_response_images: (string | null)[];
        auto_score: number | null;
        teacher_score: number | null;
        teacher_feedback: string | null;
        // Per-question dwell time accumulated during the student's attempt.
        // 0 (or absent) on legacy attempts predating the telemetry migration —
        // the time-per-question chart hides itself in that case.
        time_spent_ms?: number;
      } | null;
    }>;
  }>;
  retest: AttemptRetestState;
  // Exam-mode lockdown audit trail. Empty `events` array on attempts
  // submitted by clean candidates AND on legacy attempts predating the
  // attempt_violations table — the UI hides the integrity card in both
  // cases by checking `total === 0`.
  integrity?: {
    total: number;
    counts: Partial<Record<AttemptViolationKind, number>>;
    events: Array<{
      id: string;
      kind: AttemptViolationKind;
      occurred_at: string;
      duration_ms: number | null;
      metadata: Record<string, unknown>;
    }>;
  };
}

export type AttemptViolationKind =
  | "visibility_hidden"
  | "window_blur"
  | "fullscreen_exit"
  | "context_menu"
  | "copy";

export async function getAttempt(displayId: string, attemptId: string): Promise<AttemptDetail> {
  return http("GET", `/online-tests/${encodeURIComponent(displayId)}/submissions/${attemptId}`);
}

// Teacher decision endpoints. Optional `note` is forwarded to the student
// in the retest_approved / retest_denied notification body, useful for
// "next time use the textarea before tapping submit" type messages.
export function approveRetest(requestId: string, note: string | null = null): Promise<{ ok: true; status: "approved" }> {
  return http<{ ok: true; status: "approved" }>(
    "POST", `/online-tests/retest-requests/${encodeURIComponent(requestId)}/approve`, { note },
  );
}
export function denyRetest(requestId: string, note: string | null = null): Promise<{ ok: true; status: "denied" }> {
  return http<{ ok: true; status: "denied" }>(
    "POST", `/online-tests/retest-requests/${encodeURIComponent(requestId)}/deny`, { note },
  );
}

// Per-test retest backlog — both do-over (attempt_id set) and no-show
// (attempt_id null) requests in one list. SubmissionsPage uses this to
// surface a "Pending retest requests" banner so the teacher can act on
// no-shows inline (do-overs are also actionable inside AttemptPage).
export interface RetestRequestRow {
  id: string;
  status: "pending" | "approved" | "denied";
  attempt_id: string | null;
  no_show: boolean;
  student: { id: string; name: string; roll_no: string };
  reason: string | null;
  decision_note: string | null;
  created_at: string;
  decided_at: string | null;
}
export function listTestRetestRequests(displayId: string): Promise<{ requests: RetestRequestRow[] }> {
  return http<{ requests: RetestRequestRow[] }>(
    "GET", `/online-tests/${encodeURIComponent(displayId)}/retest-requests`,
  );
}

export async function scoreResponse(args: {
  attemptId: string;
  questionId: string;
  teacherScore: number;
  feedback?: string;
}): Promise<void> {
  await http("PATCH", `/online-tests/attempts/${args.attemptId}/responses/${args.questionId}/score`, {
    teacher_score: args.teacherScore,
    teacher_feedback: args.feedback ?? null,
  });
}

export async function finalizeAttempt(attemptId: string): Promise<void> {
  await http("POST", `/online-tests/attempts/${attemptId}/finalize`);
}

// Save the parallel annotation-URL array for a descriptive answer. Must be the
// same length as response_images; each entry is either null (un-annotated) or
// a /api/question-images/<uuid>.<ext> URL (see uploadAnnotatedImage).
export async function saveAnnotations(args: {
  attemptId: string;
  questionId: string;
  annotated_response_images: (string | null)[];
}): Promise<void> {
  await http("PATCH",
    `/online-tests/attempts/${args.attemptId}/responses/${args.questionId}/annotations`,
    { annotated_response_images: args.annotated_response_images });
}

// Upload the flattened render produced by AnnotateModal. Reuses the existing
// upload endpoint and downscaler — line strokes survive q=0.85 JPEG fine, and
// keeping the same bucket means the SSR-validated /api/question-images/ prefix
// (enforced by the PATCH endpoint above) holds.
export async function uploadAnnotatedImage(file: File): Promise<{ url: string }> {
  const shrunk = await downscaleImage(file);
  const form = new FormData();
  form.append("file", shrunk);
  return httpMultipart<{ url: string }>("/online-tests/upload-image", form);
}

// ── Student participation ──────────────────────────────────────────────────
// Roster + rolled-up activity across visible tests. The mobile Profile menu's
// "Student Participation" entry hits these endpoints; visibility is scoped on
// the server (see online_tests.ts / teacherTestScopeSql).

export interface StudentParticipationRow {
  student_id: string;
  name: string;
  roll_no: string;
  class_level: string;                  // e.g. "IX", "X" — used by the class filter pills.
  class_label: string;                  // e.g. "IX-A"
  tests_assigned: number;
  tests_attempted: number;
  total_score: number;
  total_max: number;
  avg_percent: number | null;
  // Last 5 visible-test scores as chronological percentages (oldest → newest).
  // `null` entries are scoreless attempts (max_marks was 0/unset). Rendered as
  // a row of traffic-light dots on the participation table.
  recent_pcts: (number | null)[];
}

export function listStudentParticipation(): Promise<{ students: StudentParticipationRow[] }> {
  return http("GET", "/teacher/students-participation");
}

export interface StudentAttemptRow {
  attempt_id: string;
  status: string;                       // 'in_progress' | 'submitted' | 'reviewed'
  started_at: string | null;
  submitted_at: string | null;
  auto_score: number | null;
  teacher_score: number | null;
  max_marks: number | null;
  test: {
    id: string;
    _uuid: string;
    title: string;
    subject: string;
    class_label: string;
  };
}

export function listStudentAttempts(studentId: string): Promise<{
  student: { id: string; name: string; roll_no: string; class_label: string };
  attempts: StudentAttemptRow[];
}> {
  return http("GET", `/teacher/students/${encodeURIComponent(studentId)}/attempts`);
}

// ── Student complaints ──────────────────────────────────────────────────────
// 'open' is what the backend stores for what the UI labels "Pending" — the
// value predates the four-state workflow and renaming it would need a data
// migration for no gain.
export type ComplaintStatus = "open" | "investigating" | "resolved" | "dismissed";
export type ComplaintSource =
  "roommate" | "hostel_staff" | "teacher" | "student" | "parent" | "external" | "other";
export interface ComplaintAttachment { url: string; filename?: string; mime?: string; size?: number }
export interface ComplaintStudent { student_id: string; name: string; roll_no: string; class_label: string }
export interface Complaint {
  id: string;
  about: string;
  status: ComplaintStatus;
  source: ComplaintSource | null;
  investigation_notes: string | null;
  attachments: ComplaintAttachment[];
  created_at: string;
  created_by_name: string;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  complainants: ComplaintStudent[];
  accused: ComplaintStudent[];
}

export function listComplaints(): Promise<{ complaints: Complaint[] }> {
  return http("GET", "/complaints");
}
export function listStudentComplaints(studentId: string): Promise<{ complaints: Complaint[] }> {
  return http("GET", `/students/${encodeURIComponent(studentId)}/complaints`);
}
export function createComplaint(body: {
  complainant_student_ids: string[];
  accused_student_ids: string[];
  about: string;
  source?: ComplaintSource | null;
  attachments?: ComplaintAttachment[];
}): Promise<Complaint> {
  return http("POST", "/complaints", body);
}
export function updateComplaintStatus(
  id: string, status: ComplaintStatus, resolution_note?: string,
): Promise<Complaint> {
  return http("PATCH", `/complaints/${encodeURIComponent(id)}`, { status, resolution_note });
}
// Partial patch — used for the investigation notes / source, which change
// independently of the status badge.
export function updateComplaint(
  id: string,
  patch: Partial<Pick<Complaint, "status" | "source" | "investigation_notes" | "resolution_note">>,
): Promise<Complaint> {
  return http("PATCH", `/complaints/${encodeURIComponent(id)}`, patch);
}
export function uploadComplaintAttachment(file: Blob, filename = "evidence"): Promise<ComplaintAttachment> {
  const form = new FormData();
  form.append("file", file, filename);
  return httpMultipart<ComplaintAttachment>("/complaints/attachments", form);
}

// ── Student dossier ─────────────────────────────────────────────────────────
// Everything on /students/:id that isn't an online-test attempt: identity +
// medical profile, emergency contacts, infirmary visits, discipline register,
// offline exam marks and standalone doubt tickets. One GET feeds every tab;
// each mutation returns the affected collection in full so the caller replaces
// a slice of state rather than reconciling a single row.

export interface DossierAttachment { url: string; filename?: string; mime?: string; size?: number }

export interface StudentProfile {
  id: string;
  name: string;
  roll_no: string;
  class_level: string;
  section: string;
  class_label: string;
  email: string | null;
  phone: string | null;
  student_type: "boarder" | "day_scholar";
  admission_no: string | null;
  dob: string | null;                    // YYYY-MM-DD
  gender: "male" | "female" | "other" | null;
  hostel_block: string | null;
  room_no: string | null;
  home_address: string | null;
  blood_group: string | null;
  allergies: string | null;
  chronic_conditions: string | null;
  current_medications: string | null;
  medical_notes: string | null;
  photo_url: string | null;
  profile_updated_at: string | null;
  profile_updated_by_name: string | null;
}

export type ContactRelation = "father" | "mother" | "guardian" | "local_guardian" | "sibling" | "other";
export interface StudentContact {
  id: string;
  relation: ContactRelation;
  name: string;
  phone: string | null;
  alt_phone: string | null;
  email: string | null;
  address: string | null;
  occupation: string | null;
  is_primary: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// A verified Parent-app account linked to this student. Read-only in the
// teacher app — parents own their identity in the parent app.
export interface LinkedParent {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  status: string;
  relation: string;
  verified_at: string | null;
}

export type HealthStatus = "open" | "observation" | "closed";
export interface HealthLog {
  id: string;
  visited_at: string;
  symptoms: string[];
  temperature_c: number | null;
  symptom_notes: string | null;
  action_taken: string | null;
  medication: string | null;
  referred_to: string | null;
  isolation_room: string | null;
  status: HealthStatus;
  outcome: string | null;
  closed_at: string | null;
  parent_informed: boolean;
  attachments: DossierAttachment[];
  created_by: string;
  created_by_name: string;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export type Severity = "low" | "medium" | "high";
export interface Violation {
  id: string;
  occurred_at: string;
  category: string | null;
  severity: Severity;
  points: number;
  description: string;
  action_taken: string | null;
  parent_informed: boolean;
  status: "open" | "closed";
  attachments: DossierAttachment[];
  created_by: string;
  created_by_name: string;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AcademicRecord {
  id: string;
  term: string | null;
  exam_name: string;
  subject: string;
  exam_date: string | null;
  marks: number | null;
  max_marks: number | null;
  grade: string | null;
  class_rank: number | null;
  remark: string | null;
  created_by: string;
  created_by_name: string;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export type DoubtTicketStatus = "unassigned" | "assigned" | "resolved";
export interface DoubtLink { label: string; url: string }
export interface DoubtTicket {
  id: string;
  subject: string;
  topic: string | null;
  question: string;
  attachments: DossierAttachment[];
  status: DoubtTicketStatus;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_at: string | null;
  answer: string | null;
  answer_links: DoubtLink[];
  answer_attachments: DossierAttachment[];
  answered_by: string | null;
  answered_by_name: string | null;
  answered_at: string | null;
  resolved_at: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface StudentDossier {
  student: StudentProfile;
  contacts: StudentContact[];
  parents: LinkedParent[];
  health: HealthLog[];
  health_rollup: { total: number; open: number };
  violations: Violation[];
  discipline_rollup: {
    total: number; points: number; low: number; medium: number; high: number; open: number;
  };
  academics: AcademicRecord[];
  academics_rollup: {
    records: number; scored: number; marks: number; max: number; pct: number | null;
  };
  doubts: DoubtTicket[];
  doubts_rollup: { total: number; unassigned: number; assigned: number };
}

export interface StaffMember { id: string; name: string; role: string }

const stu = (id: string) => `/teacher/students/${encodeURIComponent(id)}`;

export function getStudentDossier(studentId: string): Promise<StudentDossier> {
  return http("GET", `${stu(studentId)}/dossier`);
}

export function listStaffDirectory(): Promise<{ staff: StaffMember[] }> {
  return http("GET", "/teacher/staff-directory");
}

// ── Profile + photo ─────────────────────────────────────────────────────────

export function updateStudentProfile(
  studentId: string, patch: Partial<Omit<StudentProfile,
    "id" | "class_level" | "section" | "class_label" | "photo_url"
    | "profile_updated_at" | "profile_updated_by_name">>,
): Promise<{ student: StudentProfile }> {
  return http("PATCH", `${stu(studentId)}/profile`, patch);
}

// Downscaled client-side first — a phone camera still is ~4 MB and the server
// caps the upload at that, so a raw shot would bounce.
export async function uploadStudentPhoto(studentId: string, file: File): Promise<{ photo_url: string }> {
  const small = await downscaleImage(file, { maxEdge: 1200, quality: 0.85 });
  const form = new FormData();
  form.append("file", small, small.name || "photo.jpg");
  return httpMultipart<{ photo_url: string }>(`${stu(studentId)}/photo`, form);
}

export function removeStudentPhoto(studentId: string): Promise<void> {
  return http("DELETE", `${stu(studentId)}/photo`);
}

export async function uploadDossierAttachment(file: File): Promise<DossierAttachment> {
  // PDFs pass through untouched; images get the same downscale as the photo so
  // a staff member's evidence shot doesn't blow the 10 MB cap.
  const payload = file.type.startsWith("image/")
    ? await downscaleImage(file, { maxEdge: 1600, quality: 0.82 })
    : file;
  const form = new FormData();
  form.append("file", payload, payload.name || file.name || "attachment");
  return httpMultipart<DossierAttachment>("/teacher/dossier/attachments", form);
}

// ── Emergency contacts ──────────────────────────────────────────────────────

export type ContactInput = Partial<Omit<StudentContact, "id" | "created_by" | "created_at" | "updated_at">>;

export function createStudentContact(studentId: string, body: ContactInput): Promise<{ contacts: StudentContact[] }> {
  return http("POST", `${stu(studentId)}/contacts`, body);
}
export function updateStudentContact(contactId: string, body: ContactInput): Promise<{ contacts: StudentContact[] }> {
  return http("PATCH", `/teacher/student-contacts/${encodeURIComponent(contactId)}`, body);
}
export function deleteStudentContact(contactId: string): Promise<{ contacts: StudentContact[] }> {
  return http("DELETE", `/teacher/student-contacts/${encodeURIComponent(contactId)}`);
}

// ── Health logs ─────────────────────────────────────────────────────────────

export type HealthInput = Partial<Omit<HealthLog,
  "id" | "created_by" | "created_by_name" | "updated_by_name" | "created_at" | "updated_at" | "closed_at">>;

export function createHealthLog(studentId: string, body: HealthInput): Promise<{ health: HealthLog[] }> {
  return http("POST", `${stu(studentId)}/health-logs`, body);
}
export function updateHealthLog(logId: string, body: HealthInput): Promise<{ health: HealthLog[] }> {
  return http("PATCH", `/teacher/health-logs/${encodeURIComponent(logId)}`, body);
}
export function deleteHealthLog(logId: string): Promise<{ health: HealthLog[] }> {
  return http("DELETE", `/teacher/health-logs/${encodeURIComponent(logId)}`);
}

// ── Violations ──────────────────────────────────────────────────────────────

export type ViolationInput = Partial<Omit<Violation,
  "id" | "created_by" | "created_by_name" | "updated_by_name" | "created_at" | "updated_at">>;

export function createViolation(studentId: string, body: ViolationInput): Promise<{ violations: Violation[] }> {
  return http("POST", `${stu(studentId)}/violations`, body);
}
export function updateViolation(violationId: string, body: ViolationInput): Promise<{ violations: Violation[] }> {
  return http("PATCH", `/teacher/violations/${encodeURIComponent(violationId)}`, body);
}
export function deleteViolation(violationId: string): Promise<{ violations: Violation[] }> {
  return http("DELETE", `/teacher/violations/${encodeURIComponent(violationId)}`);
}

// ── Academic records ────────────────────────────────────────────────────────

export type AcademicInput = Partial<Omit<AcademicRecord,
  "id" | "created_by" | "created_by_name" | "updated_by_name" | "created_at" | "updated_at">>;

export function createAcademicRecord(studentId: string, body: AcademicInput): Promise<{ academics: AcademicRecord[] }> {
  return http("POST", `${stu(studentId)}/academic-records`, body);
}
export function updateAcademicRecord(recordId: string, body: AcademicInput): Promise<{ academics: AcademicRecord[] }> {
  return http("PATCH", `/teacher/academic-records/${encodeURIComponent(recordId)}`, body);
}
export function deleteAcademicRecord(recordId: string): Promise<{ academics: AcademicRecord[] }> {
  return http("DELETE", `/teacher/academic-records/${encodeURIComponent(recordId)}`);
}

// ── Doubt tickets ───────────────────────────────────────────────────────────

export type DoubtTicketInput = Partial<Pick<DoubtTicket,
  "subject" | "topic" | "question" | "attachments" | "status" | "assigned_to"
  | "answer" | "answer_links" | "answer_attachments">>;

export function createDoubtTicket(studentId: string, body: DoubtTicketInput): Promise<{ doubts: DoubtTicket[] }> {
  return http("POST", `${stu(studentId)}/doubt-tickets`, body);
}
export function updateDoubtTicket(ticketId: string, body: DoubtTicketInput): Promise<{ doubts: DoubtTicket[] }> {
  return http("PATCH", `/teacher/doubt-tickets/${encodeURIComponent(ticketId)}`, body);
}
export function deleteDoubtTicket(ticketId: string): Promise<{ doubts: DoubtTicket[] }> {
  return http("DELETE", `/teacher/doubt-tickets/${encodeURIComponent(ticketId)}`);
}

// ── Doubts (per-question student↔teacher threads) ───────────────────────────

export interface DoubtAttachment {
  kind: "image" | "audio";
  url: string;
  filename?: string;
  mime?: string;
  size?: number;
}

export interface DoubtListRow {
  id: string;
  testDisplayId: string;
  testTitle: string;
  questionId: string;
  qNo: string | number;
  qTextPreview: string;
  studentName: string;
  studentRoll: string;
  status: "open" | "answered" | "resolved";
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  messageCount: number;
  lastBody: string | null;
  lastAt: string | null;
  lastSender: "student" | "teacher" | null;
}

export interface DoubtThread {
  doubt: {
    id: string;
    attemptId: string;
    testDisplayId: string;
    testTitle: string;
    questionId: string;
    qNo: string | number;
    studentName: string;
    studentRoll: string;
    status: "open" | "answered" | "resolved";
    createdAt: string;
    updatedAt: string;
    resolvedAt: string | null;
  };
  question: {
    id: string;
    q_no: string | number;
    type: string;
    text: string;
    marks: number;
    images: { question?: string[]; solution?: string[] } | null;
    expected_answer: string | null;
    options: Array<{ id: string; letter: string; text: string; image: string | null; is_correct: boolean }>;
  } | null;
  studentResponse: { selectedOptionId: string | null; textAnswer: string | null } | null;
  messages: Array<{
    id: string;
    senderType: "student" | "teacher";
    senderName: string | null;
    body: string | null;
    attachments: DoubtAttachment[];
    createdAt: string;
  }>;
}

export function listDoubts(status?: "open" | "answered" | "resolved"): Promise<DoubtListRow[]> {
  const q = status ? `?status=${status}` : "";
  return http<DoubtListRow[]>("GET", `/teacher/doubts${q}`);
}

export function doubtCounts(): Promise<{ open: number; answered: number; resolved: number }> {
  return http("GET", "/teacher/doubts/counts");
}

export function getDoubt(doubtId: string): Promise<DoubtThread> {
  return http<DoubtThread>("GET", `/teacher/doubts/${doubtId}`);
}

export function replyToDoubt(doubtId: string, body: string, attachments: DoubtAttachment[] = []): Promise<void> {
  return http<void>("POST", `/teacher/doubts/${doubtId}/messages`, { body, attachments });
}

export function resolveDoubt(doubtId: string): Promise<void> {
  return http<void>("POST", `/teacher/doubts/${doubtId}/resolve`);
}

export async function uploadDoubtAttachment(file: File): Promise<DoubtAttachment> {
  // Image attachments go through the same downscale path as question / answer
  // uploads. Audio/PDF non-image attachments pass through untouched
  // (downscaleImage short-circuits on file.type).
  const shrunk = await downscaleImage(file);
  const form = new FormData();
  form.append("file", shrunk);
  return httpMultipart<DoubtAttachment>("/teacher/doubts/upload-attachment", form);
}

// ── Reported questions ─────────────────────────────────────────────────────
// Students can flag a broken/ambiguous question during an attempt. The admin
// panel has this in its notification bell; the mobile Doubts screen surfaces
// it as a sibling tab. Status is school-wide: when any teacher resolves a
// report it's resolved for everyone.
export interface ReportedQuestionRow {
  id: string;
  status: "open" | "resolved";
  created_at: string;
  resolved_at: string | null;
  attempt_id: string;
  test_display_id: string;
  test_title: string;
  student_name: string;
  student_roll: string;
  question_id: string;
  q_no: string | number;
  question_text: string;
  question_type: string;
  question_marks: number | null;
  note: string;
}

export function listReportedQuestions(status: "open" | "resolved" | "all" = "open"): Promise<ReportedQuestionRow[]> {
  return http<ReportedQuestionRow[]>("GET", `/reported-questions?status=${status}`);
}

export function reportedQuestionCounts(): Promise<{ open: number; resolved: number }> {
  return http("GET", "/reported-questions/count");
}

export function resolveReportedQuestion(id: string): Promise<void> {
  return http<void>("POST", `/reported-questions/${encodeURIComponent(id)}/resolve`);
}

// ── Attendance (geofenced check-in / check-out) ─────────────────────────────
// One row per teacher per work_date. hours_present is a Postgres generated
// column, so it's null until check-out lands. Backend computes Haversine
// distance against schools.geofence_{lat,lng} and rejects with human-readable
// error strings ("you're 215m away…") that AttendancePage surfaces verbatim.

export interface GeofenceConfig {
  lat: number | null;
  lng: number | null;
  radius_m: number;
  timezone: string;
  configured: boolean;
}

export interface AttendanceRow {
  id: string;
  work_date: string;                  // YYYY-MM-DD in school tz
  check_in_at: string;
  check_out_at: string | null;
  check_in_distance: number;
  check_out_distance: number | null;
  check_out_mode?: "gps" | "override";   // 'override' = "checked out anyway" from off-site
  hours_present: number | null;
}

// An announced holiday covering today, when it pauses staff attendance.
// Check-in stays open — boarding and office staff work through a break — but
// nobody is nudged, nobody appears in the absence digest, and the day is not
// counted as a working day. See backend services/holidays.ts.
export interface AttendanceHoliday {
  name: string;
  start_date: string;
  end_date: string;
}

export function getTodayAttendance(): Promise<{
  geofence: GeofenceConfig; today: AttendanceRow | null; holiday?: AttendanceHoliday | null;
}> {
  return http("GET", "/attendance/today");
}

// Standard check-in — GPS only, no selfie. Backend enforces the geofence /
// accuracy gates here. If this rejects (or the device couldn't get a fix at
// all), the page surfaces the fallback path below.
export function checkIn(body: { lat: number; lng: number; accuracy: number }): Promise<AttendanceRow> {
  return http<AttendanceRow>("POST", "/attendance/check-in", body);
}

// Fallback check-in — captured selfie + free-text reason, optionally bundling
// whatever GPS the device managed to read. The backend does NOT enforce the
// geofence radius on this path; admin reviews the selfie + reason via the
// recent-check-ins card.
export async function checkInFallback(
  reason: string,
  selfie: Blob,
  gps?: { lat: number; lng: number; accuracy: number } | null,
): Promise<AttendanceRow> {
  const form = new FormData();
  form.append("reason", reason);
  form.append("selfie", selfie, "selfie.jpg");
  if (gps) {
    form.append("lat", String(gps.lat));
    form.append("lng", String(gps.lng));
    form.append("accuracy", String(gps.accuracy));
  }
  return httpMultipart<AttendanceRow>("/attendance/check-in-fallback", form);
}

// Standard check-out enforces the geofence radius. If the teacher is outside
// it (forgot to punch out before leaving), the backend rejects with
// { distance, radius_m, can_override: true }; the page then re-calls this with
// override=true to "check out anyway". Override punches record the distance
// and notify the school's admins.
export function checkOut(
  body: { lat: number; lng: number; accuracy: number },
  opts: { override?: boolean } = {},
): Promise<AttendanceRow> {
  return http<AttendanceRow>("POST", "/attendance/check-out",
    opts.override ? { ...body, override: true } : body);
}

export function listMyAttendance(month?: string): Promise<{ month: string; rows: AttendanceRow[] }> {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return http("GET", `/attendance/my-history${q}`);
}

// Admin/HoD only. 403 from non-admin tokens; the page hides this card unless
// currentTeacher().role is admin or hod.
export function getGeofenceAdmin(): Promise<GeofenceConfig> {
  return http("GET", "/attendance/admin/geofence");
}

export function updateGeofenceAdmin(body: {
  lat: number; lng: number; radius_m: number; timezone: string;
}): Promise<GeofenceConfig> {
  return http("PATCH", "/attendance/admin/geofence", body);
}

// Admin · selfie review. Returns recent check-ins with a thumbnail URL the
// caller can <img src> directly. `selfie_url` is null on rows past the
// 30-day retention window (the server has already swept the file) AND on
// any GPS-mode row (those never had a selfie). check_in_mode lets the UI
// badge fallback rows for easy scanning.
export interface AdminCheckInRow {
  id: string;
  work_date: string;
  check_in_at: string;
  check_out_at: string | null;
  check_in_distance: number;
  check_out_distance: number | null;
  check_out_mode: "gps" | "override" | null;   // 'override' = off-site "check out anyway"
  // 'auto' = nobody punched; the scheduler asserted presence for a boarder
  // (services/attendance_reminders.ts). Such rows carry no real position:
  // lat/lng are 0,0 and check_in_distance is -1.
  check_in_mode: "gps" | "fallback" | "auto";
  check_in_fallback_reason: string | null;
  user_id: string;
  teacher_name: string;
  teacher_role: string;
  selfie_url: string | null;
}
export function listRecentCheckInsAdmin(days = 1): Promise<{ retention_days: number; rows: AdminCheckInRow[] }> {
  return http("GET", `/attendance/admin/recent-checkins?days=${encodeURIComponent(String(days))}`);
}

// ── Leave applications ──────────────────────────────────────────────────────
// Teacher files an application; admin/HoD approves or rejects. v1 has no
// balance tracking — admin sees the queue and decides per request.

export type LeaveReasonCategory = "sick" | "casual" | "personal" | "bereavement" | "other";
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancelled";
export type LeaveHalfDay = "morning" | "afternoon" | null;

export interface LeaveAttachment {
  url: string;
  filename?: string;
  mime?: string;
  size?: number;
}

export interface LeaveApplication {
  id: string;
  start_date: string;        // YYYY-MM-DD
  end_date: string;          // YYYY-MM-DD
  half_day: LeaveHalfDay;
  reason_category: LeaveReasonCategory;
  reason_note: string | null;
  attachments: LeaveAttachment[];
  status: LeaveStatus;
  decision_at: string | null;
  decision_note: string | null;
  created_at: string;
}

// One period the applicant will miss, and who they named to take it.
// cover_teacher_id null = they filed without assigning anyone, which the admin
// needs to see before approving.
export interface LeaveCover {
  cover_date: string;
  day_of_week: number;
  period_no: number;
  class_label: string;
  subject_name: string | null;
  cover_teacher_id: string | null;
  cover_teacher_name: string | null;
}

export interface AdminLeaveApplication extends LeaveApplication {
  teacher_id: string;
  teacher_name: string;
  teacher_role: string;
  decider_name: string | null;
  // Empty for a leave filed before the routine existed, and for rejected or
  // cancelled applications — their covers are deleted when retracted.
  covers: LeaveCover[];
}

export interface ApprovedLeaveRow {
  id: string;
  start_date: string;
  end_date: string;
  half_day: LeaveHalfDay;
  reason_category: LeaveReasonCategory;
  reason_note: string | null;
}

// Teacher side
export function applyForLeave(body: {
  start_date: string;
  end_date: string;
  half_day: LeaveHalfDay;
  reason_category: LeaveReasonCategory;
  reason_note: string | null;
  attachments: LeaveAttachment[];
  // Who takes each missed period, keyed by coverKey(date, period). A period may
  // be left out (or mapped to null) to file with it unstaffed — the applicant
  // is nudged about gaps, not blocked by them. Substitutes are notified as soon
  // as this lands, so cancelling or being rejected retracts them.
  covers?: Record<string, string | null>;
}): Promise<LeaveApplication & { missed_periods: number }> {
  return http("POST", "/leave/applications", body);
}

export function listMyLeaves(): Promise<{ rows: LeaveApplication[] }> {
  return http("GET", "/leave/my-applications");
}

export function listMyApprovedLeavesForMonth(month?: string): Promise<{ month: string; rows: ApprovedLeaveRow[] }> {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return http("GET", `/leave/my-approved${q}`);
}

export function cancelMyLeave(id: string): Promise<{ id: string; status: LeaveStatus }> {
  return http("POST", `/leave/applications/${encodeURIComponent(id)}/cancel`, {});
}

// Multipart upload — verification document. Mirrors the doubts attachment
// pattern; URL returned is /api/leave-attachments/<uuid>.<ext>.
export async function uploadLeaveAttachment(file: File): Promise<LeaveAttachment> {
  const form = new FormData();
  form.append("file", file);
  return httpMultipart<LeaveAttachment>("/leave/attachments", form);
}

// Admin / HoD side
export function listAdminLeaveApplications(
  status: "pending" | "approved" | "rejected" | "cancelled" | "all" = "pending",
): Promise<{ status: string; rows: AdminLeaveApplication[] }> {
  return http("GET", `/leave/admin/applications?status=${status}`);
}

export function approveLeave(id: string, note: string | null = null): Promise<void> {
  return http<void>("POST", `/leave/admin/applications/${encodeURIComponent(id)}/approve`, { note });
}

export function rejectLeave(id: string, note: string | null = null): Promise<void> {
  return http<void>("POST", `/leave/admin/applications/${encodeURIComponent(id)}/reject`, { note });
}

// ── Daily progress (per-period teaching log) ───────────────────────────────
// Chapters come from the shared catalog above (listChapters); the daily-log
// caller passes only subject_id to get every chapter for that subject across
// classes — the chapter row's class_level field tells the UI which class it
// belongs to if a label is needed.

export interface PeriodLogRow {
  id: string;
  work_date: string;                    // YYYY-MM-DD in school tz
  session_kind: "period" | "extra";
  period_no: number;
  class_section_id: string;
  class_label: string;
  // subject + chapter are optional on a period entry now (only `topic` is
  // mandatory), so these are null when the teacher logged just what they taught.
  subject_id: string | null;
  subject_name: string | null;
  chapter_id: string | null;
  chapter_number: number | null;
  chapter_name: string | null;
  topic: string;
  task_given: string | null;            // the task set for the next day, if any
  lapses: number;                       // students flagged "not studied" in this period
  created_at: string;
  updated_at: string;
}

export type LapseStatus = "not_studied" | "partial";

export interface LapseInput {
  student_id: string;
  status: LapseStatus;
  note?: string | null;
}

export interface PeriodEntryBody {
  period_no: number;
  class_section_id: string;
  subject_id: string | null;            // optional
  chapter_id: string | null;            // optional (requires subject_id)
  topic: string;                        // mandatory: "what did you teach?"
  task_given?: string | null;           // optional: task for tomorrow
  // Omit to leave existing marks untouched; send [] to clear them.
  lapses?: LapseInput[];
}

// ── The per-period page's context: roster, the task being checked, saved marks ──

export interface RosterStudent {
  student_id: string;
  name: string;
  roll_no: string;
  photo_url: string | null;
  student_type: "boarder" | "day_scholar";
}

export interface PreviousTask {
  id: string;
  work_date: string;                    // YYYY-MM-DD
  period_no: number;
  task_given: string;
  teacher_name: string;
  subject_name: string | null;
}

export interface SavedLapse {
  id: string;
  student_id: string;
  student_name: string;
  roll_no: string;
  status: LapseStatus;
  note: string | null;
  task_log_id: string | null;
  marked_at: string;
}

export interface PeriodContext {
  work_date: string;
  roster: RosterStudent[];
  previous_task: PreviousTask | null;
  lapses: SavedLapse[];                 // only when entry_id was given
}

export function getPeriodContext(args: {
  class_section_id: string; subject_id?: string | null; period_no: number; entry_id?: string | null;
}): Promise<PeriodContext> {
  const q = new URLSearchParams({ class_section_id: args.class_section_id, period_no: String(args.period_no) });
  if (args.subject_id) q.set("subject_id", args.subject_id);
  if (args.entry_id) q.set("entry_id", args.entry_id);
  return http("GET", `/progress/period-context?${q.toString()}`);
}

// A class taught OUTSIDE the fixed Periods 1–8 schedule (boarding / evening /
// remedial). One session can cover several classes; `session_group_id` is its
// identity for edit/delete. Chapter + topic are free-typed into `topic`.
export interface ExtraSessionRow {
  session_group_id: string;
  work_date: string;                    // YYYY-MM-DD in school tz
  subject_id: string;
  subject_name: string;
  topic: string;
  started_at: string;                   // "HH:MM" (24h)
  ended_at: string;                     // "HH:MM" (24h)
  classes: { id: string; label: string }[];
  photo_path: string | null;           // live classroom evidence (filename)
  created_at: string;
  updated_at: string;
}

// URL of an extra session's evidence photo, or null if none.
export function extraPhotoUrl(row: Pick<ExtraSessionRow, "photo_path">): string | null {
  return row.photo_path ? apiUrl(`/progress-photos/${row.photo_path}`) : null;
}

export interface ExtraSessionBody {
  class_section_ids: string[];
  subject_id: string;
  topic: string;
  started_at: string;                   // "HH:MM" (24h)
  ended_at: string;                     // "HH:MM" (24h)
}

export function getTodayProgress(): Promise<{ work_date: string; rows: PeriodLogRow[]; extra: ExtraSessionRow[] }> {
  return http("GET", "/progress/today");
}

export function createPeriodEntry(body: PeriodEntryBody): Promise<PeriodLogRow> {
  return http<PeriodLogRow>("POST", "/progress/entries", body);
}

export function updatePeriodEntry(id: string, body: PeriodEntryBody): Promise<PeriodLogRow> {
  return http<PeriodLogRow>("PATCH", `/progress/entries/${encodeURIComponent(id)}`, body);
}

export function deletePeriodEntry(id: string): Promise<void> {
  return http<void>("DELETE", `/progress/entries/${encodeURIComponent(id)}`);
}

function extraSessionForm(body: ExtraSessionBody): FormData {
  const form = new FormData();
  for (const id of body.class_section_ids) form.append("class_section_ids", id);
  form.append("subject_id", body.subject_id);
  form.append("topic", body.topic);
  form.append("started_at", body.started_at);
  form.append("ended_at", body.ended_at);
  return form;
}

// `photo` is the OPTIONAL live classroom still — omit it to log without evidence.
export function createExtraSession(body: ExtraSessionBody, photo?: Blob | null): Promise<ExtraSessionRow> {
  const form = extraSessionForm(body);
  if (photo) form.append("photo", photo, "classroom.jpg");
  return httpMultipart<ExtraSessionRow>("/progress/extra", form);
}

// A new `photo` is optional on edit — omit it to keep the existing evidence.
export function updateExtraSession(groupId: string, body: ExtraSessionBody, photo?: Blob | null): Promise<ExtraSessionRow> {
  const form = extraSessionForm(body);
  if (photo) form.append("photo", photo, "classroom.jpg");
  return httpMultipart<ExtraSessionRow>(`/progress/extra/${encodeURIComponent(groupId)}`, form, "PATCH");
}

export function deleteExtraSession(groupId: string): Promise<void> {
  return http<void>("DELETE", `/progress/extra/${encodeURIComponent(groupId)}`);
}

export function listMyProgressHistory(
  month?: string,
): Promise<{ month: string; rows: PeriodLogRow[]; extra: ExtraSessionRow[] }> {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return http("GET", `/progress/my-history${q}`);
}

// ── Admin / HoD · school-wide progress (every teacher's log) ────────────────
// Same row shapes as the personal feeds, plus the authoring teacher. Backed by
// /progress/school-recent (the "recent staff progress" card) and
// /progress/school-history (the "View all" page). Both re-check the role.
export type SchoolPeriodLogRow = PeriodLogRow & { user_id: string; teacher_name: string };
export type SchoolExtraSessionRow = ExtraSessionRow & { user_id: string; teacher_name: string };

export function listSchoolRecentProgress(
  limit = 12,
): Promise<{ rows: SchoolPeriodLogRow[]; extra: SchoolExtraSessionRow[] }> {
  return http("GET", `/progress/school-recent?limit=${encodeURIComponent(String(limit))}`);
}

export function listSchoolProgressHistory(
  month?: string,
): Promise<{ month: string; rows: SchoolPeriodLogRow[]; extra: SchoolExtraSessionRow[] }> {
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return http("GET", `/progress/school-history${q}`);
}

// ── Class routine (weekly timetable) ───────────────────────────────────────
// The PLAN — who teaches what, when — as opposed to the daily progress log
// above, which is what actually happened. Admins/HoDs author it in
// RoutinePage; every teacher can read a class's week and their own.
//
// day_of_week is ISO-8601: 1 = Monday … 7 = Sunday. The school week is Mon–Sat,
// so the builder renders 1–6 and never offers Sunday.
export const ROUTINE_DAYS = [
  { value: 1, short: "Mon", label: "Monday" },
  { value: 2, short: "Tue", label: "Tuesday" },
  { value: 3, short: "Wed", label: "Wednesday" },
  { value: 4, short: "Thu", label: "Thursday" },
  { value: 5, short: "Fri", label: "Friday" },
  { value: 6, short: "Sat", label: "Saturday" },
] as const;

export interface RoutineSlot {
  id: string;
  class_section_id: string;
  day_of_week: number;
  period_no: number;
  subject_id: string;
  subject_name: string;
  subject_short_code: string | null;
  // null when the period's subject is fixed but staffing is still open.
  teacher_id: string | null;
  teacher_name: string | null;
  updated_at: string;
}

export interface RoutineClassSection {
  id: string;
  class_level: string;
  section: string | null;
  academic_year: string;
  label: string;
}

// One row of the teacher picker. `assigned` marks the teachers already carrying
// this (class × subject); `busy_with` names the class that already has them in
// this exact (day, period) — non-null means the save WILL be refused, so the UI
// disables the row rather than letting the admin discover it on submit.
export interface RoutineCandidate {
  id: string;
  name: string;
  role: string;
  assigned: boolean;
  busy_with: string | null;
}

export interface RoutineSlotBody {
  class_section_id: string;
  day_of_week: number;
  period_no: number;
  subject_id: string;
  teacher_id: string | null;
}

export function getClassRoutine(
  classSectionId: string,
): Promise<{ class_section: RoutineClassSection; slots: RoutineSlot[] }> {
  return http("GET", `/routine/class-sections/${encodeURIComponent(classSectionId)}`);
}

export function listRoutineCandidates(args: {
  class_section_id: string; subject_id?: string | null; day_of_week: number; period_no: number;
}): Promise<{ candidates: RoutineCandidate[] }> {
  const q = new URLSearchParams({
    class_section_id: args.class_section_id,
    day_of_week: String(args.day_of_week),
    period_no: String(args.period_no),
  });
  if (args.subject_id) q.set("subject_id", args.subject_id);
  return http("GET", `/routine/slot-candidates?${q.toString()}`);
}

export function saveRoutineSlot(body: RoutineSlotBody): Promise<RoutineSlot> {
  return http<RoutineSlot>("PUT", "/routine/slots", body);
}

export function clearRoutineSlot(
  classSectionId: string, dayOfWeek: number, periodNo: number,
): Promise<{ ok: true }> {
  return http(
    "DELETE",
    `/routine/slots/${encodeURIComponent(classSectionId)}/${dayOfWeek}/${periodNo}`,
  );
}

/** The caller's own week across every class they're timetabled for. */
export function getMyRoutineWeek(): Promise<{ slots: Array<RoutineSlot & { class_label: string }> }> {
  return http("GET", "/routine/my-week");
}

// ── My teaching day ────────────────────────────────────────────────────────
// What this teacher is actually teaching on a given date: their own routine
// minus periods they're on leave for, PLUS periods they're covering for an
// absent colleague. The progress grid and the dashboard both read this, so
// they can't disagree about what the teacher is meant to be doing.
export interface DaySlot {
  // 'cover' = standing in for an absent colleague, named in absent_teacher_name.
  source: "routine" | "cover";
  period_no: number;
  class_section_id: string;
  class_label: string;
  subject_id: string | null;
  subject_name: string | null;
  subject_short_code: string | null;
  starts_at: string | null;   // null when the day has no timings set
  ends_at: string | null;
  absent_teacher_name: string | null;
  logged: { id: string; topic: string } | null;
}

/**
 * `configured: false` means this teacher has no routine and nothing to cover —
 * which is also how a school that hasn't built its routine yet looks. Callers
 * fall back to free-entry logging rather than showing an empty day.
 */
export function getMyTeachingDay(
  date?: string,
): Promise<{ date: string; configured: boolean; slots: DaySlot[] }> {
  const q = date ? `?date=${encodeURIComponent(date)}` : "";
  return http("GET", `/routine/my-day${q}`);
}

// ── Leave cover (who takes my classes while I'm out) ───────────────────────
export interface MissedPeriod {
  cover_date: string;
  day_of_week: number;
  period_no: number;
  class_section_id: string;
  class_label: string;
  subject_id: string | null;
  subject_name: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

export interface CoverCandidate {
  id: string;
  name: string;
  role: string;
  // Non-null = not free; the UI disables the option and shows the reason.
  busy_reason: string | null;
}

/** Key into the candidates map: "YYYY-MM-DD:period". */
export const coverKey = (cover_date: string, period_no: number) => `${cover_date}:${period_no}`;

/**
 * The periods a leave would miss, with who's free to take each. Read-only —
 * called as the applicant picks dates, before anything is written.
 */
export function getLeavePeriodPreview(args: {
  start_date: string; end_date: string; half_day?: string | null;
}): Promise<{ periods: MissedPeriod[]; candidates: Record<string, CoverCandidate[]> }> {
  const q = new URLSearchParams({ start_date: args.start_date, end_date: args.end_date });
  if (args.half_day) q.set("half_day", args.half_day);
  return http("GET", `/leave/period-preview?${q.toString()}`);
}

// ── Period timings (the bell schedule) ─────────────────────────────────────
// School-wide, not per class — one bell rings for the whole building — but
// PER-DAY, because day length varies: eight periods midweek, four on a
// Saturday. Read by everyone, edited by admin/HoD.
//
// The row set for a day IS that day's period count. A day with NO rows has not
// been configured, and the UI falls back to DEFAULT_PERIODS_PER_DAY for it —
// deliberately different from a day configured down to four.
//
// Times are "HH:MM" 24h, which <input type="time"> both emits and accepts.
export interface PeriodTime {
  day_of_week: number;  // 1 = Monday … 7 = Sunday
  period_no: number;
  starts_at: string;    // "09:00"
  ends_at: string;      // "09:40"
  updated_at: string;
}

/**
 * Shown for a day whose timings haven't been set up yet, so the routine stays
 * buildable before anyone sets a bell schedule.
 */
export const DEFAULT_PERIODS_PER_DAY = 8;

/**
 * Hard ceiling per day — matches the DB CHECK on period_no, which in turn
 * matches teacher_period_log's 1–8 range so every planned period is loggable.
 */
export const MAX_PERIODS_PER_DAY = 8;

// Routine slots that a shortened day would strand (Saturday drops to 4 → every
// class's Saturday P5–P8 has nowhere to live). The server returns these as a
// 409 BEFORE writing anything; re-sending with confirm=true deletes them along
// with the save.
export interface PeriodTimeOrphan {
  day_of_week: number;
  day_name: string;
  period_no: number;
  slots: number;
  classes: string;
}

export function getPeriodTimes(): Promise<{ periods: PeriodTime[] }> {
  return http("GET", "/routine/period-times");
}

/**
 * Replaces the school's WHOLE week of bell timings. A day omitted entirely
 * becomes unconfigured; dropping a period from a day shortens that day.
 *
 * Throws with `status: 409` and `body.orphans` when shortening a day would
 * strand routine slots — call again with `confirm: true` to remove them too.
 */
export function savePeriodTimes(
  periods: Array<{ day_of_week: number; period_no: number; starts_at: string; ends_at: string }>,
  confirm = false,
): Promise<{ periods: PeriodTime[]; removed_slots: number }> {
  return http("PUT", "/routine/period-times", { periods, confirm_remove: confirm });
}

// ── Teacher notifications feed ─────────────────────────────────────────────
// School-scoped notifications (attempt_submitted, question_reported) plus
// teacher-targeted ones (doubt_raised, doubt_message). The bell in the
// MobileShell header polls /notifications every 30 s and on open.
export type TeacherNotificationKind =
  | "attempt_submitted"
  | "question_reported"
  | "doubt_raised"
  | "doubt_message"
  // Student asked for a retest after submitting. Routed to the test author;
  // bell + push both deep-link into AttemptPage where the Approve / Deny
  // banner shows the student's reason.
  | "retest_requested"
  // A teacher checked out from outside the geofence ("check out anyway").
  // Targeted to the school's admins/HoDs; deep-links to the attendance screen.
  | "attendance_checkout_override"
  // A teacher logged a class taught outside the fixed Periods 1–8 schedule.
  // Targeted to the school's admins.
  | "progress_extra_logged"
  // Time-based attendance nudges (backend services/attendance_reminders.ts):
  // checkin ~10:45 to staff not yet in (and not on leave); checkout ~16:30
  // (Thursday ~14:30 — half-day) to staff in but not out. Both deep-link to
  // /attendance.
  | "attendance_checkin_reminder"
  | "attendance_checkout_reminder"
  // Admin/HoD punch feed — one per teacher check-in and one per check-out.
  // Deep-link to /attendance/checkins (Recent check-ins).
  | "attendance_checked_in"
  | "attendance_checked_out"
  // Admin/HoD daily digest (~11:15) of staff with no check-in and no approved
  // leave. payload.names carries the roll-call.
  | "attendance_absent_report"
  // A teacher filed a leave application; routed to admins/HoDs, whose approval
  // queue sits inline on /attendance.
  | "leave_application_raised"
  // The outcome of the caller's OWN leave application. Deep-links to
  // /attendance/leaves.
  | "leave_application_approved"
  | "leave_application_rejected"
  // Ad-hoc notice from an admin/HoD ("Staff meeting today 3 PM"), plus the
  // optional pre-event nudge. Both deep-link to /notices.
  | "staff_notice"
  | "staff_notice_reminder"
  // A teacher used "forgot password"; routed to admins/HoDs who approve the
  // renewal. Deep-links to /password-requests.
  | "password_reset_requested"
  // A teacher self-registered and is awaiting admin approval (admins act in the
  // admin panel; on mobile this just surfaces in the bell).
  | "teacher_registration_pending"
  // A parent wrote in their "Ask a teacher" thread. Targeted at the ONE teacher
  // they picked by subject, never school-wide. Deep-links to /parent-doubts/:id.
  | "parent_chat_message"
  // A parent raised a grievance in the Parent app. Fans out to admins/HoDs
  // only; deep-links to the /parent-complaints queue.
  | "parent_complaint_raised";

export interface TeacherNotification {
  id: string;
  kind: TeacherNotificationKind;
  payload: Record<string, any>;
  created_at: string;
  read_at: string | null;
}

export function listNotifications(): Promise<{ items: TeacherNotification[]; unread: number }> {
  return http("GET", "/notifications");
}

// Full, filterable history for the notifications page (same per-user visibility
// as the bell, plus date-range / type filters and offset paging).
export interface NotificationFeedFilter {
  from?: string;        // YYYY-MM-DD (school tz), inclusive
  to?: string;          // YYYY-MM-DD (school tz), inclusive
  kind?: string;
  limit?: number;
  offset?: number;
}

export function listNotificationsFeed(
  f: NotificationFeedFilter = {},
): Promise<{ items: TeacherNotification[]; has_more: boolean }> {
  const q = new URLSearchParams();
  if (f.from)  q.set("from", f.from);
  if (f.to)    q.set("to", f.to);
  if (f.kind)  q.set("kind", f.kind);
  if (f.limit  != null) q.set("limit", String(f.limit));
  if (f.offset != null) q.set("offset", String(f.offset));
  const qs = q.toString();
  return http("GET", `/notifications/feed${qs ? `?${qs}` : ""}`);
}

export function markNotificationRead(id: string): Promise<void> {
  return http<void>("POST", `/notifications/${encodeURIComponent(id)}/read`);
}

export function markAllNotificationsRead(): Promise<void> {
  return http<void>("POST", "/notifications/read-all");
}

// ── Staff notices ───────────────────────────────────────────────────────────
// Ad-hoc messages an admin/HoD sends to staff ("Staff meeting today 3 PM").
// Composing is admin/HoD-only; every authenticated user can LIST, but a
// non-privileged caller only sees notices addressed to them.

export type NoticeCategory = "general" | "meeting" | "deadline" | "urgent";

export interface StaffNotice {
  id: string;
  title: string;
  body: string;
  category: NoticeCategory;
  audience: "all" | "selected";
  event_at: string | null;
  // Pre-rendered in the SCHOOL's timezone by the server ("today 3:00 PM") —
  // don't recompute this from event_at on the device, or a teacher travelling
  // outside IST would read a different day to everyone else.
  when_label: string | null;
  remind_before_min: number | null;
  reminder_sent_at: string | null;
  recipient_count: number;
  author_name: string;
  is_mine: boolean;
  created_at: string;
}

export interface CreateNoticeInput {
  title: string;
  body?: string;
  category: NoticeCategory;
  audience: "all" | "selected";
  recipient_ids?: string[];
  event_at?: string | null;        // ISO instant
  remind_before_min?: number | null;
}

export function listNotices(
  opts: { limit?: number; offset?: number } = {},
): Promise<{ items: StaffNotice[]; has_more: boolean; can_compose: boolean }> {
  const q = new URLSearchParams();
  if (opts.limit  != null) q.set("limit", String(opts.limit));
  if (opts.offset != null) q.set("offset", String(opts.offset));
  const qs = q.toString();
  return http("GET", `/notices${qs ? `?${qs}` : ""}`);
}

export function createNotice(
  input: CreateNoticeInput,
): Promise<StaffNotice & { recipient_count: number; reminder_skipped: boolean }> {
  return http("POST", "/notices", input);
}

// ── Preview-as-student ────────────────────────────────────────────────────
// Mirrors the student `/student/tests/:displayId` payload but is teacher-
// scoped, doesn't create an attempt, and includes the answer key so the
// author can sanity-check while previewing. Backed by GET
// /api/online-tests/:displayId/preview.

export interface PreviewOption {
  id: string; letter: string; text: string;
  image: string | null; is_correct: boolean;
}
export interface PreviewQuestion {
  id: string;
  q_no: string | number;
  type: "mcq" | "tf" | "short" | "long" | "fill" | "match" | "numerical" | "derivation" | "diagram";
  text: string;
  marks: number;
  images: { question: string[]; solution: string[] } | null;
  expected_answer: string | null;
  // Passage block this question belongs to, if any. Standalone → null. The
  // preview renderer dedupes consecutive items sharing the same passage_id so
  // the passage card shows once per block.
  passage_id?: string | null;
  passage?: { id: string; text: string; images?: string[] } | null;
  options: PreviewOption[];
}
export interface PreviewSection {
  id: string;
  label: string;
  title: string | null;
  instructions: string | null;
  marksPerQuestion: number | null;
  questions: PreviewQuestion[];
}
export interface PreviewPayload {
  test: {
    id: string;
    _uuid: string;
    title: string;
    subject: string;
    className: string;
    totalMarks: number | null;
    type: "blitz" | "classical" | "descriptive" | null;
    engine_config: any;     // EditableEngineConfig shape, but kept loose here
  };
  sections: PreviewSection[];
}

export function previewTest(displayId: string): Promise<PreviewPayload> {
  return http<PreviewPayload>("GET", `/online-tests/${encodeURIComponent(displayId)}/preview`);
}

// ── Surgical per-question edit ────────────────────────────────────────────
// Patches only the cells of one questions row (and optionally its
// question_options rows). Safe to call on published tests with live
// attempts — backend route bypasses persistManualStructure so nothing
// CASCADE-deletes. Any subset of fields may be omitted.

export interface QuestionPatch {
  text?: string;
  expected_answer?: string | null;
  images?: { question: string[]; solution: string[] } | null;
  // Per-option updates. id is mandatory; only the cells you send change.
  // The backend rejects patches that would leave >1 option marked correct,
  // and it refuses ids that don't already belong to this question.
  options?: Array<{
    id: string;
    text?: string;
    is_correct?: boolean;
    image?: string | null;
  }>;
}
export interface PatchedQuestion {
  id: string;
  q_no: string;
  text: string;
  expected_answer: string | null;
  images: { question: string[]; solution: string[] } | null;
  options?: Array<{
    id: string; letter: string; text: string;
    is_correct: boolean; image: string | null;
  }>;
}

// Present only when the patch flipped `is_correct` on at least one option.
// Caller surfaces these numbers as a re-grade toast so the teacher knows
// the answer-key fix propagated to already-submitted attempts.
export interface RegradeSummary {
  affected_attempts: number;
  score_changed: number;
}
export function patchQuestion(
  displayId: string, questionId: string, patch: QuestionPatch,
): Promise<{ ok: true; question: PatchedQuestion; regrade: RegradeSummary | null }> {
  return http(
    "PATCH",
    `/online-tests/${encodeURIComponent(displayId)}/questions/${encodeURIComponent(questionId)}`,
    patch,
  );
}

// uploadQuestionImage is defined earlier (around line 252) — it downscales
// the file before POSTing to /online-tests/upload-image. Reused by the
// EditQuestionSheet image rail.

// ── Test drafts ────────────────────────────────────────────────────────────
// A draft is the Create-wizard's React state captured BEFORE the test is
// published. Two callers from CreatePage:
//   1. Manual "Save draft" — explicit button in step 1/step 2.
//   2. Silent auto-save on Gemini failure — so 20 pasted questions aren't lost.
// On resume, the wizard fetches the full payload and rehydrates its state.
// Photos in the payload are URLs (uploaded to /api/question-images/ before
// save); the wizard re-fetches them as blobs on resume so re-running Gemini
// still works.

export interface DraftSummary {
  id: string;
  title: string;
  last_failure_reason: string | null;
  created_at: string;
  updated_at: string;
  section_count: number;     // populated from either sectionInputs OR parsed.sections
  question_count: number;    // total questions across parsed sections; 0 pre-parse
  current_step: number;      // 1..5 — drives the resume jump
}

export interface DraftFull {
  id: string;
  title: string;
  payload: any;              // opaque to backend — the wizard's JSON state
  last_failure_reason: string | null;
  created_at: string;
  updated_at: string;
}

/** List the caller's drafts, newest first. Lightweight — no payload. */
export async function listDrafts(): Promise<DraftSummary[]> {
  return http<DraftSummary[]>("GET", "/draft-tests");
}

/** Load a single draft for resume. 404 → caller drops the local entry. */
export async function getDraft(id: string): Promise<DraftFull> {
  return http<DraftFull>("GET", `/draft-tests/${encodeURIComponent(id)}`);
}

/** Upsert. Pass `id` to update; omit to create. Returns the (possibly new) id. */
export async function saveDraft(body: {
  id?: string;
  title?: string;
  payload: any;
  last_failure_reason?: string | null;
}): Promise<{ id: string }> {
  return http<{ id: string }>("POST", "/draft-tests", body);
}

/** Discard. 204 / 404 both let the caller drop the local entry. */
export async function deleteDraft(id: string): Promise<void> {
  return http<void>("DELETE", `/draft-tests/${encodeURIComponent(id)}`);
}

// ── Question registry ──────────────────────────────────────────────────────
// Cross-test view: for each chapter the school has published tests against,
// surface the question count and let the teacher drill in to see every
// question across every test in that chapter (unified, tabulated). Tests
// with no chapter tag land in an "Unassigned" bucket.

export interface RegistryChapter {
  key: string;              // opaque grouping key — pass back to the questions endpoint? no, we
                            // pass (subject_id, class_level, label) instead since that's how the
                            // bucket is identified server-side.
  label: string;            // human display label — "Unassigned" or the first-seen variant
  subject: string;
  subject_id: string;
  class_level: string | null;
  test_count: number;
  question_count: number;
  last_published_at: string | null;
  // Populated when the chapter name matches the curated catalog (chapters
  // table). Lets the UI show "Ch. 3 — Light" instead of just the label.
  catalog: { id: string; chapter_number: number } | null;
}

/** List every chapter that has at least one published test in the teacher's school. */
export function listChapterRegistry(opts: {
  subject_id?: string;
  class_level?: string;
}): Promise<RegistryChapter[]> {
  const q = new URLSearchParams();
  if (opts.subject_id) q.set("subject_id", opts.subject_id);
  if (opts.class_level) q.set("class_level", opts.class_level);
  const qs = q.toString();
  return http<RegistryChapter[]>("GET", `/question-registry/chapters${qs ? `?${qs}` : ""}`);
}

export interface RegistryQuestion {
  id: string;
  q_no: string;
  type: "mcq" | "tf" | "short" | "long" | string;
  text: string;
  marks: number;
  images: { question: string[]; solution: string[] } | null;
  options: Array<{ letter: string; text: string; is_correct: boolean }>;
  section: { label: string; title: string | null };
  test: {
    id: string;
    display_id: string;
    title: string;
    scheduled_date: string | null;
    created_at: string;
    chapter: string | null;
    topic: string | null;
  };
  // Aggregate attempt stats across every submitted+reviewed attempt on the
  // test that owns this question. `total` is the denominator (how many
  // students attempted the test); the four buckets sum to it. Descriptive
  // questions land in `pending` while waiting for teacher review.
  stats: {
    total: number;
    correct: number;
    wrong: number;
    pending: number;
    skipped: number;
  };
}

// Per-student response for the pie-chart drill-in.
export interface RegistryQuestionResponse {
  student: {
    id: string;
    name: string;
    roll_no: string;
    class_level: string | null;
    section: string | null;
  };
  attempt: {
    id: string;
    status: "submitted" | "reviewed" | string;
    submitted_at: string | null;
  };
  response: {
    // null for descriptive / skipped
    selected_letter: string | null;
    // null when no option picked; otherwise whether the picked option was the
    // marked-correct one (lets the table show "✓ B" vs "✗ A → correct: C").
    selected_is_correct: boolean | null;
    text_answer: string | null;
    auto_score: number | null;
    teacher_score: number | null;
    time_spent_ms: number;
  };
  outcome: "correct" | "wrong" | "pending" | "skipped";
}

/** Pull every question across every test in the school for the given bucket. */
export function listChapterQuestions(opts: {
  subject_id: string;
  class_level?: string | null;
  // Empty/omitted = the Unassigned bucket. Otherwise the display label
  // from listChapterRegistry; the backend lowercases-trims for matching.
  chapter_label?: string | null;
}): Promise<{ questions: RegistryQuestion[] }> {
  const q = new URLSearchParams();
  q.set("subject_id", opts.subject_id);
  if (opts.class_level) q.set("class_level", opts.class_level);
  if (opts.chapter_label != null) q.set("chapter_label", opts.chapter_label);
  return http<{ questions: RegistryQuestion[] }>("GET", `/question-registry/questions?${q.toString()}`);
}

/** Per-student responses for one question — drives the pie-chart drill-in. */
export function listQuestionResponses(questionId: string): Promise<{
  question: { id: string; text: string; type: string; marks: number; test_display_id: string };
  responses: RegistryQuestionResponse[];
}> {
  return http("GET", `/question-registry/questions/${encodeURIComponent(questionId)}/responses`);
}

// ── Student attendance — the daily class register ────────────────────────────
// The teacher's own punch clock lives further up this file (getTodayAttendance,
// checkIn/checkOut). This section is the OTHER attendance: who was in class.

export type StudentAttendanceStatus =
  "present" | "absent" | "late" | "half_day" | "excused";

export const ATTENDANCE_STATUS_LABEL: Record<StudentAttendanceStatus, string> = {
  present: "Present", absent: "Absent", late: "Late",
  half_day: "Half day", excused: "Excused",
};

// One letter per status — what a register cell holds, matching the codes the
// server prints in the PDF and the spreadsheet so the three never drift.
export const ATTENDANCE_STATUS_CODE: Record<StudentAttendanceStatus, string> = {
  present: "P", absent: "A", late: "L", half_day: "H", excused: "E",
};

export interface AttendanceClassRow {
  id: string;
  class_level: string;
  section: string | null;
  academic_year: string;
  label: string;
  students: number;
  marked: number;
  absent: number;
  /** Marks the gate reader made that no teacher has confirmed yet. */
  from_gate: number;
  last_marked_at: string | null;
  last_marked_by: string | null;
}

export interface AttendanceSheetStudent {
  student_id: string;
  name: string;
  roll_no: string;
  photo_url: string | null;
  status: StudentAttendanceStatus | null;
  note: string | null;
  marked_at: string | null;
  updated_at: string | null;
  marked_by_name: string | null;
  /** 'teacher' | 'rfid' | null. 'rfid' = the gate suggested it; nobody has confirmed. */
  source: "teacher" | "rfid" | null;
  /** The gate tap this mark came from, when it came from one. */
  first_scan_at: string | null;
}

export interface AttendanceSheet {
  date: string;
  can_mark: boolean;
  class_section: { id: string; class_level: string; section: string | null; label: string };
  students: AttendanceSheetStudent[];
  summary: {
    students: number; marked: number; not_marked: number;
    /** Still awaiting a human. Saving the sheet clears this to zero. */
    from_gate: number;
  } & Record<StudentAttendanceStatus, number>;
}

export function listAttendanceClasses(date?: string): Promise<{
  date: string; can_mark_any: boolean; classes: AttendanceClassRow[];
}> {
  const p = new URLSearchParams();
  if (date) p.set("date", date);
  const qs = p.toString();
  return http("GET", `/student-attendance/classes${qs ? `?${qs}` : ""}`);
}

export function getAttendanceSheet(
  class_section_id: string, date?: string,
): Promise<AttendanceSheet> {
  const p = new URLSearchParams({ class_section_id });
  if (date) p.set("date", date);
  return http("GET", `/student-attendance/sheet?${p.toString()}`);
}

export function saveAttendance(
  class_section_id: string, date: string,
  marks: Array<{ student_id: string; status: StudentAttendanceStatus; note?: string | null }>,
): Promise<{ ok: true; date: string; saved: number }> {
  return http("POST", "/student-attendance/mark", { class_section_id, date, marks });
}

// Un-mark one child. A delete, not a sixth status — "never marked" and "marked
// something" print differently on the register.
export function clearAttendanceMark(student_id: string, date: string): Promise<{ ok: true }> {
  const p = new URLSearchParams({ student_id, date });
  return http("DELETE", `/student-attendance/mark?${p.toString()}`);
}

// ── The register (report + printable sheets) ────────────────────────────────

export interface AttendanceReportQuery {
  from?: string;
  to?: string;
  class_section_id?: string;
  student_id?: string;
  status?: StudentAttendanceStatus;
  q?: string;
}

export interface AttendanceReportCell {
  status: StudentAttendanceStatus;
  note: string | null;
  marked_at: string;
  marked_by_name: string | null;
  corrected: boolean;
  source: "teacher" | "rfid";
  first_scan_at: string | null;
}

export interface AttendanceReportTotals {
  present: number; absent: number; late: number;
  half_day: number; excused: number;
  marked: number; not_marked: number;
  percent: number | null;
}

export interface AttendanceReportStudent {
  student_id: string;
  name: string;
  roll_no: string;
  class_section_id: string;
  class_label: string;
  cells: Record<string, AttendanceReportCell>;
  totals: AttendanceReportTotals;
}

export interface AttendanceReport {
  filters: {
    from: string; to: string;
    class_section_id: string | null; student_id: string | null;
    status: StudentAttendanceStatus | null; q: string | null;
  };
  labels: { class_section: string | null; status: string | null; student: string | null };
  days: string[];
  students: AttendanceReportStudent[];
  detail: Array<{
    day: string; mark_id: string; student_id: string; name: string; roll_no: string;
    class_label: string; status: StudentAttendanceStatus; note: string | null;
    marked_at: string; updated_at: string | null; marked_by_name: string | null;
  }>;
  detail_truncated: boolean;
  summary: {
    by_day: Array<{ day: string; percent: number | null } & Omit<AttendanceReportTotals, "percent">>;
    by_class: Array<{
      class_section_id: string; class_label: string;
      present: number; absent: number; late: number; half_day: number;
      excused: number; marked: number; percent: number | null;
    }>;
    totals: AttendanceReportTotals & {
      students: number; days: number; cells: number;
      calendar_days: number; days_without_marks: number;
      unconfirmed: number;
    };
  };
  generated_at: string;
}

function attendanceReportQs(q: AttendanceReportQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function getAttendanceReport(q: AttendanceReportQuery): Promise<AttendanceReport> {
  return http("GET", `/student-attendance/report${attendanceReportQs(q)}`);
}

/** The path the two export buttons hand back to the server — same query, three renderings. */
export function attendanceExportPath(q: AttendanceReportQuery, ext: "pdf" | "xlsx"): string {
  return `/student-attendance/report.${ext}${attendanceReportQs(q)}`;
}

// A file the server generates — the register PDF and the spreadsheet.
//
// Not an <a href>: the session is a Bearer token, and a plain link carries no
// headers, so the browser would fetch the URL unauthenticated and get a 401.
// Fetching it here and handing the browser a blob keeps ONE authentication path
// for the whole app — no download tokens, no credentials in a query string,
// nothing new to expire or leak into a server log. Same approach as the
// other apps' downloadFile().
export async function downloadFile(
  path: string, fallbackName: string,
): Promise<{ filename: string; blobUrl: string; type: string }> {
  const res = await fetch(apiUrl(path), { headers: authHeaders() });
  if (!res.ok) {
    if (res.status === 401) onSessionEnded();
    let msg = `GET ${path} → ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
    throw Object.assign(new Error(msg), { status: res.status });
  }
  // The server names the file; the caller's name is only a fallback for a proxy
  // that strips the header.
  const cd = res.headers.get("content-disposition") || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const blob = await res.blob();
  return {
    filename: m?.[1] || fallbackName,
    blobUrl: URL.createObjectURL(blob),
    type: res.headers.get("content-type") || blob.type,
  };
}

// ── RFID gate readers + cards ───────────────────────────────────────────────
// The office surface. Taking the roll needs none of this — the gate fills the
// sheet in and the class teacher confirms it there.

export interface RfidReader {
  id: string;
  label: string;
  location: string | null;
  active: boolean;
  last_seen_at: string | null;
  last_ip: string | null;
  firmware: string | null;
  created_at: string;
  /** null = never seen. The number the health screen leads with. */
  minutes_since_seen: number | null;
  scans_today: number;
}

export interface RfidCard {
  /** The Ledger's permanent student id — the link to where the card is edited. */
  ledger_student_uid: string | null;
  id: string;
  card_uid: string;
  label: string | null;
  issued_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
  student_id: string;
  name: string;
  roll_no: string;
  class_label: string;
  issued_by_name: string | null;
  last_seen_at: string | null;
}

export interface RfidUnknownScan {
  card_uid: string;
  scanned_at: string;
  reader_id: string;
  reader_label: string;
  times_seen: number;
}

export function listRfidReaders(): Promise<{ readers: RfidReader[] }> {
  return http("GET", "/rfid/readers");
}

// The `key` in this response exists nowhere else, ever — it is flashed into one
// device and cannot be recovered. The UI must show it before anything can
// navigate away.
export function createRfidReader(
  body: { label: string; location?: string },
): Promise<{ reader: RfidReader; key: string }> {
  return http("POST", "/rfid/readers", body);
}

export function rotateRfidReaderKey(
  id: string,
): Promise<{ reader: { id: string; label: string }; key: string; note: string }> {
  return http("POST", `/rfid/readers/${encodeURIComponent(id)}/rotate`);
}

export function updateRfidReader(
  id: string, patch: { label?: string; location?: string; active?: boolean },
): Promise<{ reader: RfidReader }> {
  return http("PATCH", `/rfid/readers/${encodeURIComponent(id)}`, patch);
}

export function deleteRfidReader(id: string): Promise<{ ok: true }> {
  return http("DELETE", `/rfid/readers/${encodeURIComponent(id)}`);
}

// Cards seen at a gate that belong to nobody — the enrolment queue. Hand a
// child a blank card, have them tap it, and it appears here.
export function listRfidUnknownScans(
  minutes = 15,
): Promise<{ minutes: number; scans: RfidUnknownScan[] }> {
  return http("GET", `/rfid/unknown-scans?minutes=${minutes}`);
}

export function listRfidCards(
  f: { q?: string; student_id?: string; revoked?: boolean } = {},
): Promise<{ cards: RfidCard[] }> {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.student_id) p.set("student_id", f.student_id);
  if (f.revoked) p.set("revoked", "1");
  const qs = p.toString();
  return http("GET", `/rfid/cards${qs ? `?${qs}` : ""}`);
}

export interface RfidSettings {
  late_after: string | null;
  grace_min: number;
  timezone: string;
  /** Period-1 start per weekday — what a blank late_after follows. */
  period_one_starts: Array<{ day_of_week: number; starts_at: string }>;
}

export function getRfidSettings(): Promise<RfidSettings> {
  return http("GET", "/rfid/settings");
}

export function updateRfidSettings(
  body: { late_after: string | null; grace_min: number },
): Promise<{ ok: true; late_after: string | null; grace_min: number }> {
  return http("PUT", "/rfid/settings", body);
}

// ──────────────────────────────────────────────────────────────────────────
// Announcements to parents (admin + HoD)
// ──────────────────────────────────────────────────────────────────────────
// The same API the admin panel's composer uses — deliberately not a second
// one, so validation and the parent fan-out cannot drift between the two.

export interface ParentAnnouncement {
  id: string;
  scope: "school" | "class" | "section";
  title: string;
  body: string;
  published_at: string;
  pinned_until: string | null;
  /** Set from the admin panel only; shown here, never changed from here. */
  publish_to_website: boolean;
  author_role: string;
  author_name: string;
  /** "VIII-A", or "VIII-A, VII-B" when it went to several; null school-wide. */
  audience_label: string | null;
  /** Files sent with the notice; parents open them from their Notices tab. */
  attachments: AnnouncementAttachment[];
  /** Server's rule: an admin may delete any notice, an HoD only their own. */
  can_delete: boolean;
}

export interface AnnouncementClass { id: string; label: string; academic_year: string | null }

/** A file banked by uploadAnnouncementAttachment, ready to publish with. */
export interface AnnouncementAttachment {
  url: string;
  filename?: string;
  mime?: string;
  size?: number;
}

/** Matches MAX_ATTACHMENTS in backend/src/routes/admin_announcements.ts. */
export const MAX_ANNOUNCEMENT_ATTACHMENTS = 6;

// Banked one at a time, before the notice is published: on a village
// connection a failed upload should cost the file, not the typed notice.
// Photos are downscaled in-browser first; a PDF goes up as it is.
export async function uploadAnnouncementAttachment(file: File): Promise<AnnouncementAttachment> {
  const payload = file.type.startsWith("image/")
    ? await downscaleImage(file, { maxEdge: 1600, quality: 0.82 })
    : file;
  const form = new FormData();
  form.append("file", payload, payload.name || file.name || "attachment");
  return httpMultipart<AnnouncementAttachment>("/admin/announcements/attachments", form);
}

export function listParentAnnouncements(): Promise<ParentAnnouncement[]> {
  return http<ParentAnnouncement[]>("GET", "/admin/announcements");
}

export function listAnnouncementClasses(): Promise<AnnouncementClass[]> {
  return http<AnnouncementClass[]>("GET", "/admin/announcements/classes");
}

export function publishParentAnnouncement(input: {
  scope: "school" | "class";
  title: string;
  body: string;
  /** Every class a class-scoped notice goes to. Empty for a school-wide one. */
  class_section_ids: string[];
  pinned_until: string | null;
  attachments?: AnnouncementAttachment[];
}): Promise<{ id: string; published_at: string; class_count: number }> {
  return http("POST", "/admin/announcements", input);
}

export function deleteParentAnnouncement(id: string): Promise<{ ok: true }> {
  return http("DELETE", `/admin/announcements/${id}`);
}

// ── Examinations: offline marks entry ───────────────────────────────────────
//
// The teacher's half of the marks system (backend/src/routes/exams.ts). The
// admin builds an exam out of PAPERS — one per class-section × subject, each
// with its own written and oral maximums — and names the teacher who enters
// each one. What arrives here is that teacher's to-do list.
//
// The lifecycle a teacher can see: a paper is `pending` until they submit it,
// then `submitted` and locked. Only the office can reopen it, and only the
// office can publish the exam to parents — nothing a teacher does here is
// visible to a parent until that happens.

export interface ExamPaperRow {
  id: string;
  exam_id: string;
  exam_name: string;
  exam_type: string;
  /** 'entry_open' | 'published'. Drafts never reach this list. */
  exam_status: string;
  academic_year: string;
  starts_on: string | null;
  class_label: string;
  /**
   * The subject AS EXAMINED, which is not always what the subjects registry
   * calls it — a paper may be renamed for the exam, or exist only in
   * examinations with no registry row behind it at all. Resolved server-side;
   * just display it.
   */
  subject_name: string;
  /** null for a paper with no registry subject behind it. */
  short_code: string | null;
  exam_date: string | null;
  max_written: number;
  max_oral: number;
  max_total: number;
  status: "pending" | "submitted";
  submitted_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  /** Class strength, and how many of them have a mark or an absent flag. */
  roll: number;
  entered: number;
}

export interface ExamSheetStudent {
  student_id: string;
  roll_no: string;
  name: string;
  written: number | null;
  oral: number | null;
  is_absent: boolean;
  /** written + oral, or null when absent / nothing entered. Server-computed. */
  total: number | null;
  remark: string | null;
  updated_at: string | null;
}

export interface ExamSheet {
  paper: {
    id: string;
    exam_id: string;
    exam_name: string;
    exam_type: string;
    exam_status: string;
    class_label: string;
    class_section_id: string;
    subject_name: string;
    short_code: string | null;
    exam_date: string | null;
    max_written: number;
    max_oral: number;
    max_total: number;
    status: "pending" | "submitted";
    assigned_to: string | null;
    assigned_to_name: string | null;
    submitted_at: string | null;
    submitted_by_name: string | null;
  };
  students: ExamSheetStudent[];
}

export interface ExamMarkInput {
  student_id: string;
  written?: number | null;
  oral?: number | null;
  is_absent?: boolean;
  remark?: string | null;
}

/** Every paper this teacher owes. Privileged roles get the whole school's. */
export function listMyExamPapers(): Promise<ExamPaperRow[]> {
  return http("GET", "/teacher/exam-papers");
}

export function getExamSheet(paperId: string): Promise<ExamSheet> {
  return http("GET", `/teacher/exam-papers/${encodeURIComponent(paperId)}`);
}

/**
 * Save the sheet. Partial is fine — the teacher fills what they have and comes
 * back. The server upserts on (paper, student), so a double-tap after a dropped
 * connection corrects the sheet rather than doubling it.
 */
export function saveExamMarks(
  paperId: string, marks: ExamMarkInput[],
): Promise<{ ok: true; saved: number } & ExamSheet> {
  return http("PUT", `/teacher/exam-papers/${encodeURIComponent(paperId)}/marks`, { marks });
}

/**
 * Hand the sheet in. Rejects with 409 and a `missing` list if any child on the
 * CURRENT roll has neither a mark nor an absent flag — see the error shape the
 * entry screen unpacks.
 */
export function submitExamPaper(paperId: string): Promise<{ ok: true } & ExamSheet> {
  return http("POST", `/teacher/exam-papers/${encodeURIComponent(paperId)}/submit`, {});
}

// ── Parent doubts (parent↔teacher threads) ──────────────────────────────────
//
// The staff side of "Ask a teacher" in the parent app. A parent picks the
// subject teacher assigned to their child's class and asks; the thread belongs
// to that one teacher.
//
// Distinct from listDoubts() above, which is the STUDENT's per-question doubt
// anchored to one attempt. These have no question behind them — they are the
// parent's own questions about their child's work.

export interface ParentChatAttachment {
  kind?: "image" | "file";
  url: string;
  filename?: string;
  mime?: string;
  size?: number;
}

export interface ParentDoubtRow {
  id: string;
  parent_id: string;
  parent_name: string;
  parent_phone: string | null;
  student_id: string;
  student_name: string;
  roll_no: string | null;
  class_label: string | null;
  teacher_id: string;
  teacher_name: string;
  subject_id: string;
  subject_name: string;
  last_message_at: string | null;
  last_message_body: string | null;
  last_message_has_files: boolean | null;
  unread_count: number;
  /**
   * Whether THIS user may post in the thread. False for an admin/HoD reading
   * someone else's thread — the message table records only 'parent' | 'teacher'
   * and no author id, so a reply from the office would reach the parent
   * captioned as their child's subject teacher.
   */
  can_reply: boolean;
}

export interface ParentDoubtMessage {
  id: string;
  sender_kind: "parent" | "teacher";
  /** Null when the message is a bare photograph. */
  body: string | null;
  attachments: ParentChatAttachment[];
  created_at: string;
  read_at: string | null;
}

export function listParentDoubts(): Promise<ParentDoubtRow[]> {
  return http<ParentDoubtRow[]>("GET", "/teacher/parent-chats");
}

export function parentDoubtCounts(): Promise<{ threads: number; unanswered: number }> {
  return http("GET", "/teacher/parent-chats/counts");
}

export function getParentDoubt(chatId: string): Promise<ParentDoubtRow> {
  return http<ParentDoubtRow>("GET", `/teacher/parent-chats/${encodeURIComponent(chatId)}`);
}

export function listParentDoubtMessages(
  chatId: string, opts: { before?: string; limit?: number } = {},
): Promise<ParentDoubtMessage[]> {
  const q = new URLSearchParams();
  if (opts.before) q.set("before", opts.before);
  if (opts.limit)  q.set("limit", String(opts.limit));
  return http<ParentDoubtMessage[]>(
    "GET", `/teacher/parent-chats/${encodeURIComponent(chatId)}/messages?${q.toString()}`,
  );
}

export function replyToParentDoubt(
  chatId: string, body: string, attachments: ParentChatAttachment[] = [],
): Promise<void> {
  return http<void>(
    "POST", `/teacher/parent-chats/${encodeURIComponent(chatId)}/messages`, { body, attachments },
  );
}

export async function uploadParentDoubtAttachment(file: File): Promise<ParentChatAttachment> {
  // Images go through the same downscale path as the question / answer uploads
  // so a 12 MP phone shot is not pushed down a village connection at full size.
  // PDFs pass through untouched — re-encoding one as a JPEG would destroy it.
  const form = new FormData();
  if (file.type === "application/pdf") {
    form.append("file", file, file.name);
  } else {
    const small = await downscaleImage(file);
    form.append("file", small, file.name.replace(/\.[^.]+$/, "") + ".jpg");
  }
  return httpMultipart<ParentChatAttachment>("/teacher/parent-chats/upload-attachment", form);
}

// ── Parent complaints (grievance register) ──────────────────────────────────
//
// Raised by a parent in the parent app, triaged by admin/HoD here. The API
// refuses everyone else, so the menu entry is hidden for plain teachers rather
// than leading them to a 403.
//
// NOT the student discipline register (listComplaints / the /complaints
// endpoints) — that one is a teacher logging one student against another.

export type ParentComplaintStatus = "open" | "investigating" | "resolved" | "dismissed";

export interface ParentComplaintRow {
  id: string;
  category: string;
  about: string;
  attachments: ParentChatAttachment[];
  status: ParentComplaintStatus;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  parent_id: string;
  parent_name: string;
  parent_phone: string | null;
  student_id: string | null;
  student_name: string | null;
  student_roll: string | null;
  student_class: string | null;
  resolved_by_name: string | null;
}

export const PARENT_COMPLAINT_CATEGORY_LABEL: Record<string, string> = {
  academics:  "Teaching & studies",
  teacher:    "A member of staff",
  facilities: "School facilities",
  transport:  "Bus & transport",
  fees:       "Fees & payments",
  safety:     "Safety & wellbeing",
  other:      "Something else",
};

export const PARENT_COMPLAINT_STATUS_LABEL: Record<ParentComplaintStatus, string> = {
  open:          "Pending",
  investigating: "Investigating",
  resolved:      "Resolved",
  dismissed:     "Closed",
};

export function listParentComplaints(
  status?: ParentComplaintStatus,
): Promise<{ complaints: ParentComplaintRow[] }> {
  const q = status ? `?status=${status}` : "";
  return http("GET", `/admin/parent-complaints${q}`);
}

export function parentComplaintCounts(): Promise<{
  open: number; investigating: number; resolved: number; dismissed: number; total: number;
}> {
  return http("GET", "/admin/parent-complaints/counts");
}

/**
 * Partial update. `resolution_note` is REQUIRED by the server when moving to
 * resolved / dismissed — closing a family's complaint without a word back is
 * the failure the register exists to prevent.
 */
export function updateParentComplaint(
  id: string, body: { status?: ParentComplaintStatus; resolution_note?: string | null },
): Promise<ParentComplaintRow> {
  return http("PATCH", `/admin/parent-complaints/${encodeURIComponent(id)}`, body);
}
